import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Upload,
  Download,
  Plus,
  Edit,
  Trash2,
  FileText,
  Star,
  Share2,
  Eye,
  EyeOff,
  Archive,
  Trophy,
} from "lucide-react";
import { debounce } from "lodash";
import parse, { domToReact } from "html-react-parser";
import { FixedSizeList as List } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";
import { UserProvider, useUser } from "./hooks/useUser";
import PremiumModalContainer from "./components/PremiumModalContainer";
import ServiceUnavailableModal from "./components/ServiceUnavailableModal";
import AdminPanel from "./components/AdminPanel";
import AddTorrentModal from "./components/AddTorrentModal";
import ReplaceTorrentModal from "./components/ReplaceTorrentModal";
import StarfieldBackground from "./components/StarfieldBackground";
import CyberToggle from "./components/CyberToggle";
import GlobalSearchResults from "./components/GlobalSearchResults";
import ProfileModal from "./components/ProfileModal";
import SaveToFavoritesModal from "./components/SaveToFavoritesModal";
import FloatingDownloadBar from "./components/FloatingDownloadBar";
import ArchiveManagerModal from "./components/ArchiveManagerModal";
import TorrentImageGallery from "./components/TorrentImageGallery";
import TorrentFileTree from "./components/TorrentFileTree";
import TopCarousel from "./components/TopCarousel";
import TopManageModal from "./components/TopManageModal";
import HelpModal from "./components/HelpModal";
import BackToTopButton from "./components/BackToTopButton";
import SourceMarquee from "./components/SourceMarquee";
import AdminFilterButton, {
  getTrackerFilterLabel,
} from "./components/AdminFilterButton";
import AppSwitcherBar from "./components/AppSwitcherBar";
import HeaderWordmark from "./components/HeaderWordmark";
import { showSuccessToast, showErrorToast } from "./utils/toast";
import {
  getHighResImageUrl,
  getPrimaryTorrentImageUrl,
  getTorrentImageUrls,
} from "./utils/torrentImages";

const CATEGORIES = [
  { id: "tutorials", label: "Tutorials" },
  { id: "apps", label: "Apps" },
  { id: "plugins", label: "Plugins" },
  { id: "models", label: "Models" },
  { id: "misc", label: "Misc" },
  { id: "materials", label: "Materials" },
  { id: "gamedev", label: "GameDev" },
  { id: "exclusive", label: "Exclusive" },
];

// Компонент для редактирования изображений торрента
const ImageEditor = ({
  torrentId,
  currentImageUrl,
  currentImageUrls = [],
  onSave,
  onCancel,
}) => {
  const [imageUrls, setImageUrls] = useState(
    currentImageUrls.length > 0 ? currentImageUrls : [currentImageUrl || ""],
  );
  const [saving, setSaving] = useState(false);
  const { user } = useUser();

  useEffect(() => {
    setImageUrls(
      currentImageUrls.length > 0 ? currentImageUrls : [currentImageUrl || ""],
    );
  }, [currentImageUrl, currentImageUrls]);

  const normalizedImageUrls = imageUrls
    .map((imageUrl) => imageUrl.trim())
    .filter(Boolean)
    .slice(0, 5);

  const handleImageUrlChange = (index, value) => {
    setImageUrls((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  const handleAddField = () => {
    setImageUrls((prev) => {
      if (prev.length >= 5) {
        return prev;
      }

      return [...prev, ""];
    });
  };

  const handleRemoveField = (index) => {
    setImageUrls((prev) => {
      if (prev.length === 1) {
        return [""];
      }

      return prev.filter((_, currentIndex) => currentIndex !== index);
    });
  };

  const handleSave = async () => {
    if (normalizedImageUrls.length === 0) return;

    setSaving(true);
    try {
      const response = await fetch(`/api/admin/torrents/${torrentId}/image`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `tma ${window.Telegram.WebApp.initData}`,
        },
        body: JSON.stringify({
          image_url: normalizedImageUrls[0],
          image_urls: normalizedImageUrls,
        }),
      });

      if (response.ok) {
        const result = await response.json();
        onSave(
          result.image_url || normalizedImageUrls[0],
          result.image_urls || normalizedImageUrls,
        );
      } else {
        console.error("Failed to update image");
      }
    } catch (error) {
      console.error("Error updating image:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      handleSave();
    } else if (e.key === "Escape") {
      onCancel();
    }
  };

  return (
    <div
      className="absolute top-2 right-2 z-20 bg-gray-900/90 backdrop-blur-sm border border-gray-600 rounded-lg p-3"
      style={{ minWidth: "320px", maxWidth: "420px" }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="text-white text-sm font-medium">Edit Image URLs:</div>
        <button
          type="button"
          onClick={handleAddField}
          disabled={saving || imageUrls.length >= 5}
          className="inline-flex items-center gap-1 rounded border border-gray-600 bg-gray-800 px-2 py-1 text-xs font-medium text-white transition-colors hover:border-blue-500 disabled:opacity-40"
        >
          <Plus size={12} />
          Add
        </button>
      </div>
      <div className="space-y-2">
        {imageUrls.map((imageUrl, index) => (
          <div key={index} className="flex items-center gap-2">
            <div className="w-20 shrink-0 text-xs font-medium text-gray-300">
              {index + 1}.{index === 0 ? " primary" : ""}
            </div>
            <input
              type="text"
              value={imageUrl}
              onChange={(e) => handleImageUrlChange(index, e.target.value)}
              onKeyDown={handleKeyPress}
              placeholder={`Enter image URL ${index + 1}...`}
              className="w-full bg-gray-800 text-white border border-gray-600 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
              autoFocus={index === 0}
            />
            {index > 0 && (
              <button
                type="button"
                onClick={() => handleRemoveField(index)}
                disabled={saving}
                className="rounded border border-gray-600 bg-gray-800 p-1 text-gray-300 transition-colors hover:border-red-500 hover:text-red-400 disabled:opacity-40"
                aria-label={`Remove image ${index + 1}`}
              >
                <Trash2 size={12} />
              </button>
            )}
          </div>
        ))}
      </div>
      <div className="mt-2 text-[11px] text-gray-400">
        Up to 5 images. The first image stays primary for TOP and simple views.
      </div>
      <div className="flex gap-2 mt-2">
        <button
          onClick={handleSave}
          disabled={saving || normalizedImageUrls.length === 0}
          className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white px-2 py-1 rounded text-sm font-medium"
        >
          {saving ? "Saving..." : "Save"}
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-2 py-1 rounded text-sm font-medium"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// Компонент для редактирования названия и описания торрента
const TIME_MONTHS = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

const parseTorrentTime = (timeString) => {
  const now = new Date();
  const fallback = {
    month: TIME_MONTHS[now.getMonth()],
    day: String(now.getDate()).padStart(2, "0"),
    year: String(now.getFullYear()),
    hour: String(now.getHours()).padStart(2, "0"),
    minute: String(now.getMinutes()).padStart(2, "0"),
  };

  if (!timeString || typeof timeString !== "string") {
    return fallback;
  }

  const match = timeString
    .trim()
    .match(/^([A-Za-z]{3})\s+(\d{1,2})\s+(\d{4}),\s*(\d{1,2}):(\d{2})$/);

  if (!match) {
    return fallback;
  }

  const [, monthRaw, dayRaw, year, hourRaw, minute] = match;
  const month =
    TIME_MONTHS.find((m) => m.toLowerCase() === monthRaw.toLowerCase()) ||
    fallback.month;

  return {
    month,
    day: String(Math.min(31, Math.max(1, parseInt(dayRaw, 10)))).padStart(
      2,
      "0",
    ),
    year,
    hour: String(Math.min(23, Math.max(0, parseInt(hourRaw, 10)))).padStart(
      2,
      "0",
    ),
    minute: String(Math.min(59, Math.max(0, parseInt(minute, 10)))).padStart(
      2,
      "0",
    ),
  };
};

const formatTorrentTime = ({ month, day, year, hour, minute }) => {
  return `${month} ${String(day).padStart(2, "0")} ${year}, ${String(hour).padStart(2, "0")}:${String(minute).padStart(2, "0")}`;
};

const DetailsEditor = ({
  torrentId,
  currentTitle,
  currentDescription,
  currentTime,
  currentCategory,
  currentSize,
  onSave,
  onCancel,
}) => {
  const categories = [
    { id: "tutorials", label: "Tutorials" },
    { id: "apps", label: "Apps" },
    { id: "plugins", label: "Plugins" },
    { id: "models", label: "Models" },
    { id: "misc", label: "Misc" },
    { id: "materials", label: "Materials" },
    { id: "gamedev", label: "GameDev" },
  ];

  const initialTime = parseTorrentTime(currentTime);
  const [title, setTitle] = useState(currentTitle || "");
  const [description, setDescription] = useState(currentDescription || "");
  const [timeMonth, setTimeMonth] = useState(initialTime.month);
  const [timeDay, setTimeDay] = useState(initialTime.day);
  const [timeYear, setTimeYear] = useState(initialTime.year);
  const [timeHour, setTimeHour] = useState(initialTime.hour);
  const [timeMinute, setTimeMinute] = useState(initialTime.minute);
  const [category, setCategory] = useState(currentCategory || "tutorials");
  const [size, setSize] = useState(currentSize || "");
  const [saving, setSaving] = useState(false);

  const daysInMonth = (() => {
    const monthIndex = TIME_MONTHS.indexOf(timeMonth);
    const yearNum = parseInt(timeYear, 10) || new Date().getFullYear();
    if (monthIndex < 0) return 31;
    return new Date(yearNum, monthIndex + 1, 0).getDate();
  })();

  const handleSave = async () => {
    if (!title.trim()) return;

    const dayNum = Math.min(
      daysInMonth,
      Math.max(1, parseInt(timeDay, 10) || 1),
    );
    let yearNum = parseInt(timeYear, 10);
    if (!yearNum || Number.isNaN(yearNum)) {
      yearNum = new Date().getFullYear();
    }
    yearNum = Math.min(2100, Math.max(2000, yearNum));

    const hourNum = Math.min(23, Math.max(0, parseInt(timeHour, 10) || 0));
    const minuteNum = Math.min(59, Math.max(0, parseInt(timeMinute, 10) || 0));

    const formattedTime = formatTorrentTime({
      month: timeMonth,
      day: dayNum,
      year: yearNum,
      hour: hourNum,
      minute: minuteNum,
    });

    setSaving(true);
    try {
      const response = await fetch(`/api/admin/torrents/${torrentId}/details`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `tma ${window.Telegram.WebApp.initData}`,
        },
        body: JSON.stringify({
          title: title.trim(),
          description: description.trim(),
          time: formattedTime,
          category,
          size: size.trim(),
        }),
      });

      if (response.ok) {
        onSave({
          title: title.trim(),
          description: description.trim(),
          time: formattedTime,
          category,
          size: size.trim(),
        });
      } else {
        console.error("Failed to update details");
      }
    } catch (error) {
      console.error("Error updating details:", error);
    } finally {
      setSaving(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Escape") {
      onCancel();
    }
  };

  const selectClass =
    "bg-gray-800 text-white border border-gray-600 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500";

  return (
    <div
      className="bg-gray-900 border border-gray-600 rounded p-3 shadow-lg"
      style={{ minWidth: "350px" }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="text-white text-sm mb-2 font-medium">Edit Details:</div>

      <div className="mb-3">
        <label className="text-white text-xs mb-1 block">Title:</label>
        <input
          type="text"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder="Enter title..."
          className="w-full bg-gray-800 text-white border border-gray-600 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
          autoFocus
        />
      </div>

      <div className="mb-3">
        <label className="text-white text-xs mb-1 block">Description:</label>
        <textarea
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder="Enter description..."
          rows={4}
          className="w-full bg-gray-800 text-white border border-gray-600 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500 resize-y min-h-[96px]"
        />
      </div>

      <div className="mb-3">
        <label className="text-white text-xs mb-1 block">Category:</label>
        <select
          value={category}
          onChange={(e) => setCategory(e.target.value)}
          className={`w-full ${selectClass}`}
        >
          {categories.map((cat) => (
            <option key={cat.id} value={cat.id}>
              {cat.label}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-3">
        <label className="text-white text-xs mb-1 block">Time:</label>
        <div className="grid grid-cols-3 gap-2 mb-2">
          <select
            value={timeMonth}
            onChange={(e) => {
              setTimeMonth(e.target.value);
              const maxDay = new Date(
                parseInt(timeYear, 10) || new Date().getFullYear(),
                TIME_MONTHS.indexOf(e.target.value) + 1,
                0,
              ).getDate();
              if (parseInt(timeDay, 10) > maxDay) {
                setTimeDay(String(maxDay).padStart(2, "0"));
              }
            }}
            className={selectClass}
            aria-label="Month"
          >
            {TIME_MONTHS.map((month) => (
              <option key={month} value={month}>
                {month}
              </option>
            ))}
          </select>
          <input
            type="number"
            value={timeDay}
            onChange={(e) => setTimeDay(e.target.value)}
            onBlur={() => {
              const dayNum = parseInt(timeDay, 10);
              if (!dayNum || Number.isNaN(dayNum)) {
                setTimeDay("01");
                return;
              }
              setTimeDay(
                String(Math.min(daysInMonth, Math.max(1, dayNum))).padStart(
                  2,
                  "0",
                ),
              );
            }}
            onKeyDown={handleKeyPress}
            min={1}
            max={daysInMonth}
            placeholder="DD"
            className={selectClass}
            aria-label="Day"
          />
          <input
            type="number"
            value={timeYear}
            onChange={(e) => setTimeYear(e.target.value)}
            onKeyDown={handleKeyPress}
            min={2000}
            max={2100}
            placeholder="YYYY"
            className={selectClass}
            aria-label="Year"
          />
        </div>
        <div className="grid grid-cols-2 gap-2">
          <input
            type="number"
            value={timeHour}
            onChange={(e) => setTimeHour(e.target.value)}
            onBlur={() => {
              const hourNum = parseInt(timeHour, 10);
              if (Number.isNaN(hourNum)) {
                setTimeHour("00");
                return;
              }
              setTimeHour(
                String(Math.min(23, Math.max(0, hourNum))).padStart(2, "0"),
              );
            }}
            onKeyDown={handleKeyPress}
            min={0}
            max={23}
            placeholder="HH"
            className={selectClass}
            aria-label="Hour"
          />
          <input
            type="number"
            value={timeMinute}
            onChange={(e) => setTimeMinute(e.target.value)}
            onBlur={() => {
              const minuteNum = parseInt(timeMinute, 10);
              if (Number.isNaN(minuteNum)) {
                setTimeMinute("00");
                return;
              }
              setTimeMinute(
                String(Math.min(59, Math.max(0, minuteNum))).padStart(2, "0"),
              );
            }}
            onKeyDown={handleKeyPress}
            min={0}
            max={59}
            placeholder="MM"
            className={selectClass}
            aria-label="Minute"
          />
        </div>
        <p className="mt-1 text-[11px] text-gray-500">
          {formatTorrentTime({
            month: timeMonth,
            day: Math.min(daysInMonth, Math.max(1, parseInt(timeDay, 10) || 1)),
            year: timeYear || "----",
            hour: String(
              Math.min(23, Math.max(0, parseInt(timeHour, 10) || 0)),
            ).padStart(2, "0"),
            minute: String(
              Math.min(59, Math.max(0, parseInt(timeMinute, 10) || 0)),
            ).padStart(2, "0"),
          })}
        </p>
      </div>

      <div className="mb-3">
        <label className="text-white text-xs mb-1 block">Size:</label>
        <input
          type="text"
          value={size}
          onChange={(e) => setSize(e.target.value)}
          onKeyDown={handleKeyPress}
          placeholder="1.5GB"
          className="w-full bg-gray-800 text-white border border-gray-600 rounded px-2 py-1 text-sm focus:outline-none focus:border-blue-500"
        />
      </div>

      <div className="flex gap-2">
        <button
          onClick={handleSave}
          disabled={saving || !title.trim()}
          className="flex-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white px-2 py-1 rounded text-sm font-medium"
        >
          {saving ? "Saving..." : "Save"}
        </button>
        <button
          onClick={onCancel}
          className="flex-1 bg-gray-600 hover:bg-gray-700 text-white px-2 py-1 rounded text-sm font-medium"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

// --- Компонент для рендера html с поддержкой спойлеров и цитат ---
function renderCommentHtml(html) {
  if (!html) return null;
  // Удаляем экранированные \\n и пробелы по краям
  const cleanHtml = (html || "").replace(/\\\\n/g, "\\n").trim();
  return <CommentHtmlRenderer html={cleanHtml} />;
}

function CommentHtmlRenderer({ html }) {
  const [openSpoilers, setOpenSpoilers] = useState({});
  let spoilerIdx = -1; // This variable will link a spoiler anchor to its content

  function transform(node, idx) {
    if (node.type === "tag") {
      // Ссылка Show для спойлера
      if (
        node.name === "a" &&
        node.attribs &&
        node.attribs.onclick &&
        node.attribs.onclick.includes("BBCode.spoiler")
      ) {
        spoilerIdx++; // Increment for a new spoiler unit (link + block)
        const currentSpoilerKey = spoilerIdx; // Use this key for state
        return (
          <a
            key={idx}
            href="#"
            onClick={(e) => {
              e.preventDefault();
              setOpenSpoilers((s) => ({
                ...s,
                [currentSpoilerKey]: !s[currentSpoilerKey],
              }));
            }}
            style={{ color: "#4af", cursor: "pointer" }}
          >
            {domToReact(node.children, { transform })}
          </a>
        );
      }

      // Спойлер-блок (uses the spoilerIdx from the preceding link)
      if (
        node.name === "blockquote" &&
        node.attribs &&
        node.attribs.class &&
        node.attribs.class.includes("spoiler")
      ) {
        // DO NOT increment spoilerIdx here. Use the one from the link.
        const currentSpoilerKey = spoilerIdx;
        return (
          <blockquote
            key={idx}
            style={{
              display: openSpoilers[currentSpoilerKey] ? "block" : "none",
              background: "#222",
              color: "#ccc",
              padding: "10px",
              borderRadius: "6px",
              margin: "10px 0",
              whiteSpace: "pre-line",
            }}
          >
            {domToReact(node.children, { transform })}
          </blockquote>
        );
      }

      // Обычная цитата (ensure it's not a spoiler blockquote)
      if (node.name === "blockquote") {
        // This condition is now fine because the spoiler blockquote is handled above.
        return (
          <blockquote
            key={idx}
            style={{
              background: "#333",
              color: "#eee",
              padding: "10px",
              borderLeft: "4px solid #888",
              borderRadius: "6px",
              margin: "10px 0",
              whiteSpace: "pre-line",
            }}
          >
            {domToReact(node.children, { transform })}
          </blockquote>
        );
      }
      // Other tags will be handled by returning undefined below, allowing default parsing.
    }
    return undefined; // For text nodes, or tags not handled above, let parser handle.
  }

  return (
    <div
      style={{
        wordBreak: "break-word",
        overflowWrap: "anywhere",
        color: "#999",
      }}
    >
      {parse(html, {
        transform: (node, idx) => {
          if (node.type === "tag" && node.name === "a") {
            return (
              <a
                key={idx}
                href={node.attribs.href}
                target="_blank"
                rel="noopener noreferrer"
                style={{
                  color: "#6666ff",
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                }}
              >
                {domToReact(node.children)}
              </a>
            );
          }
          return transform(node, idx);
        },
      })}
    </div>
  );
}

// --- Новый компонент для комментариев ---
function CommentsSection({ torrentId }) {
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    fetch(`/api/content/${torrentId}/comments`)
      .then((res) => res.json())
      .then((data) => setComments(Array.isArray(data) ? data : []))
      .catch(() => setComments([]))
      .finally(() => setLoading(false));
  }, [torrentId]);

  if (loading) return <div style={{ color: "#999" }}>Loading comments...</div>;

  if (!comments.length)
    return (
      <div style={{ color: "#666", textAlign: "center" }}>No comments yet</div>
    );

  return (
    <div className="space-y-4">
      {comments.map((comment) => (
        <CommentItem key={comment.id} comment={comment} />
      ))}
    </div>
  );
}

function CommentItem({ comment }) {
  const [spoilerOpen, setSpoilerOpen] = useState(false);
  const html = (comment.body || "").replace(/\n/g, "\n").trim();
  const spoilerRegex =
    /<a[^>]*onclick="BBCode\.spoiler\(this\);"[^>]*>(.*?)<\/a>\s*<blockquote class="hidden spoiler">([\s\S]*?)<\/blockquote>/i;
  const spoilerMatch = html.match(spoilerRegex);
  let content;
  if (spoilerMatch) {
    content = (
      <div>
        <strong
          dangerouslySetInnerHTML={{ __html: html.split(spoilerMatch[0])[0] }}
        />
        <a
          href="#"
          style={{ color: "#4af", cursor: "pointer" }}
          onClick={(e) => {
            e.preventDefault();
            setSpoilerOpen((o) => !o);
          }}
        >
          {spoilerMatch[1]}
        </a>
        {spoilerOpen && (
          <blockquote
            style={{
              background: "#222",
              color: "#ccc",
              padding: 10,
              borderRadius: 6,
              margin: "10px 0",
              whiteSpace: "pre-line",
            }}
            dangerouslySetInnerHTML={{ __html: spoilerMatch[2] }}
          />
        )}
        <span
          dangerouslySetInnerHTML={{ __html: html.split(spoilerMatch[0])[1] }}
        />
      </div>
    );
  } else {
    content = html.includes("<blockquote>") ? (
      <span
        dangerouslySetInnerHTML={{
          __html: html.replace(
            /<blockquote>([\s\S]*?)<\/blockquote>/g,
            '<blockquote style="background:#333;color:#eee;padding:10px;border-left:4px solid #888;border-radius:6px;margin:10px 0;white-space:pre-line;">$1</blockquote>',
          ),
        }}
      />
    ) : (
      <span dangerouslySetInnerHTML={{ __html: html }} />
    );
  }
  return (
    <div className="mb-4 rounded-lg overflow-hidden">
      <div
        className="flex justify-between items-center px-3 py-1"
        style={{ backgroundColor: "#232323" }}
      >
        <span className="font-medium" style={{ color: "#e0e0e0" }}>
          {comment.author}
        </span>
        <span className="text-sm" style={{ color: "#999" }}>
          {comment.comment_time
            ? new Date(comment.comment_time).toLocaleDateString()
            : ""}
        </span>
      </div>
      <div
        className="px-3 pb-3 pt-2"
        style={{
          backgroundColor: "#292929",
          color: "#ccc",
          wordBreak: "break-word",
          overflowWrap: "anywhere",
        }}
      >
        {/* Ensure links in comments are blue */}
        {typeof content === "string" ? (
          <span
            dangerouslySetInnerHTML={{
              __html: content.replace(
                /<a /g,
                '<a style="color:#6666ff;word-break:break-word;overflow-wrap:anywhere;" ',
              ),
            }}
          />
        ) : (
          content
        )}
      </div>
    </div>
  );
}

// Detail Modal Component
const DetailModal = ({ item, onClose }) => {
  const [showContent, setShowContent] = useState(false);
  const [comments, setComments] = useState([]);
  const [showAccessDenied, setShowAccessDenied] = useState(false);
  const [showServiceUnavailable, setShowServiceUnavailable] = useState(false);
  const [isEditingDetails, setIsEditingDetails] = useState(false);
  const [showReplaceTorrent, setShowReplaceTorrent] = useState(false);
  const [showArchiveManager, setShowArchiveManager] = useState(false);
  const [currentTitle, setCurrentTitle] = useState(item.title);
  const [currentDescription, setCurrentDescription] = useState(
    item.description,
  );
  const [currentTime, setCurrentTime] = useState(item.time);
  const [currentCategory, setCurrentCategory] = useState(item.category);
  const [currentSize, setCurrentSize] = useState(item.size);
  const scrollContainerRef = useRef(null);
  const [scrollTarget, setScrollTarget] = useState(null);
  const [fileTreeRefreshKey, setFileTreeRefreshKey] = useState(0);
  const { user, hasAccess, isAdmin, isBanned } = useUser();

  // Determine if torrent file exists (torrent_path is set)
  const hasTorrent = !!item.torrent_path;
  // Determine if archive files are linked
  const hasArchive =
    Array.isArray(item.archive_message_ids) &&
    item.archive_message_ids.length > 0;

  const galleryImages = getTorrentImageUrls(item);

  useEffect(() => {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => setShowContent(true));
    });
    document.body.style.overflow = "hidden";

    // Очищаем MainButton при монтировании
    if (window.Telegram?.WebApp) {
      const tg = window.Telegram.WebApp;
      tg.MainButton.hide();
      tg.MainButton.offClick();
    }

    // Загружаем комментарии из БД
    fetch(`/api/content/${item.id}/comments`)
      .then((res) => res.json())
      .then(setComments)
      .catch(() => setComments([]));

    return () => {
      document.body.style.overflow = "";
      // Очищаем MainButton при размонтировании
      if (window.Telegram?.WebApp) {
        const tg = window.Telegram.WebApp;
        tg.MainButton.hide();
        tg.MainButton.offClick();
      }
    };
  }, [item.id]);

  const handleDownload = async () => {
    console.log("Download button clicked for torrent:", item.id);

    // Проверяем доступ пользователя
    if (!hasAccess()) {
      console.log("Access denied for user:", user?.telegram_id);
      setShowAccessDenied(true);
      return false;
    }

    console.log("Starting download for user:", user?.telegram_id);
    try {
      // Скачиваем торрент через новый API с проверкой трекеров
      const response = await fetch(`/api/torrents/download/${item.id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `tma ${window.Telegram?.WebApp?.initData || ""}`,
        },
        body: JSON.stringify({}),
      });

      if (response.status === 429) {
        const data = await response.json().catch(() => ({}));
        showErrorToast(
          "Daily limit reached",
          data.message || "Daily limit of 60 unique torrents reached.",
        );
        return false;
      }

      if (response.status === 403) {
        const data = await response.json().catch(() => ({}));
        if (data.code === "BANNED") {
          showErrorToast(
            "Downloads paused",
            "Downloading is paused for this account.",
          );
          return false;
        }
        setShowAccessDenied(true);
        return false;
      }

      if (response.status === 503) {
        setShowServiceUnavailable(true);
        return false;
      }

      if (!response.ok) {
        throw new Error("Download failed");
      }

      const result = await response.json();

      if (result.success) {
        return true;
      } else {
        throw new Error(result.message || "Download failed");
      }
    } catch (error) {
      console.error("Error downloading:", error);
      return false;
    }
  };

  const handleDownloadArchive = async () => {
    if (!hasAccess()) {
      setShowAccessDenied(true);
      return false;
    }

    try {
      const response = await fetch(`/api/torrents/send-archive/${item.id}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `tma ${window.Telegram?.WebApp?.initData || ""}`,
        },
        body: JSON.stringify({}),
      });
      if (response.status === 403) {
        setShowAccessDenied(true);
        return false;
      }
      if (!response.ok) throw new Error("Archive send failed");
      const result = await response.json();
      if (result.success) {
        return true;
      } else {
        throw new Error(result.error || "Archive send failed");
      }
    } catch (error) {
      console.error("Error sending archive:", error);
      return false;
    }
  };

  const handleEditDetailsClick = (e) => {
    e.stopPropagation();
    setIsEditingDetails(true);
  };

  const handleArchiveManagerClick = (e) => {
    e.stopPropagation();
    setShowArchiveManager(true);
  };

  const handleReplaceTorrentClick = (e) => {
    e.stopPropagation();
    setShowReplaceTorrent(true);
  };

  const handleReplaceTorrentSuccess = (updatedTorrent) => {
    // Обновляем локальные данные торрента если это необходимо
    console.log("Torrent file replaced successfully:", updatedTorrent);
    setShowReplaceTorrent(false);
    setFileTreeRefreshKey((key) => key + 1);
  };

  const handleDetailsSave = ({
    title: newTitle,
    description: newDescription,
    time: newTime,
    category: newCategory,
    size: newSize,
  }) => {
    setCurrentTitle(newTitle);
    setCurrentDescription(newDescription);
    setCurrentTime(newTime);
    setCurrentCategory(newCategory);
    setCurrentSize(newSize);
    setIsEditingDetails(false);
  };

  const handleDetailsCancelEdit = () => {
    setIsEditingDetails(false);
  };

  // Поделиться торрентом (короткая ссылка)
  const handleShare = () => {
    const shareUrl = `https://t.me/CGPEERSSITE_BOT/Cgpeersapp?startapp=torrent_${item.id}`;

    // Копируем в буфер обмена
    navigator.clipboard
      .writeText(shareUrl)
      .then(() => {
        showSuccessToast("Link copied!");
      })
      .catch(() => {
        // Fallback для старых браузеров
        const textArea = document.createElement("textarea");
        textArea.value = shareUrl;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
        showSuccessToast("Link copied!");
      });
  };

  return (
    <div
      className="fixed inset-0 z-[80] flex items-center justify-center"
      style={{
        touchAction: "none",
        overscrollBehavior: "contain",
        backgroundColor: showContent ? "rgba(0,0,0,0.7)" : "rgba(0,0,0,0)",
        transition: "background-color 250ms ease-out",
      }}
      onTouchMove={(e) => e.preventDefault()}
    >
      <div
        className="relative w-full h-full md:w-2/3 md:h-[95vh] lg:w-1/2 lg:max-w-[40rem] lg:h-[95vh] rounded-2xl overflow-hidden shadow-2xl"
        style={{
          backgroundColor: "#141418",
          opacity: showContent ? 1 : 0,
          transform: showContent ? "scale(1)" : "scale(0.97)",
          transition: "opacity 200ms ease-out, transform 200ms ease-out",
        }}
      >
        {/* Share button */}
        <button
          onClick={handleShare}
          className="modal-share-button"
          aria-label="Share torrent"
          title="Share"
        >
          <Share2 size={18} />
        </button>

        <button
          onClick={onClose}
          className="modal-close-button"
          aria-label="Close modal"
          title="Close"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="20"
            height="20"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>

        <div
          ref={(el) => {
            scrollContainerRef.current = el;
            setScrollTarget(el);
          }}
          className="h-full overflow-y-auto pb-32"
          style={{
            overscrollBehavior: "contain",
            WebkitOverflowScrolling: "touch",
            willChange: "transform",
          }}
        >
          {/* увеличен паддинг для кнопки */}
          {/* Image Section */}
          <div
            className="relative h-72 md:h-96"
            style={{ backgroundColor: "#282828" }}
          >
            <TorrentImageGallery
              images={galleryImages}
              alt={item.title}
              className="w-full h-full"
              imageClassName="w-full h-full object-cover"
              imageStyle={{ height: "100%", objectFit: "cover" }}
              dotContainerStyle={{ bottom: 60 }}
            />
            <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-[#141418] via-[#141418]/80 to-transparent pt-20">
              {/* Single line meta: Category • Time • Size */}
              <div
                className="flex items-center justify-center w-full px-1 mb-2"
                style={{
                  color: "rgba(255, 255, 255, 0.7)",
                  fontSize: "0.95rem",
                  fontWeight: "500",
                  letterSpacing: "0.02em",
                  textShadow: "0 1px 4px rgba(0,0,0,0.8)",
                  fontFamily:
                    "SF Pro Display, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif",
                }}
              >
                <span>{currentCategory}</span>
                <span className="mx-2 opacity-60">•</span>
                <span>{formatTimeAgo(currentTime)}</span>
                <span className="mx-2 opacity-60">•</span>
                <span>{currentSize}</span>
              </div>
            </div>
          </div>

          {/* Info Elements (REMOVED - moved to image overlay) */}

          {/* Content Section */}
          <div
            className={`p-6 m-4 rounded-xl relative -mt-4 z-10`}
            style={{
              backgroundColor: "#1a1a20",
              border: "1px solid rgba(255, 255, 255, 0.05)",
            }}
          >
            <div className="flex items-start justify-between mb-4">
              <h2
                className="text-2xl font-bold flex-1"
                style={{ color: "#999" }}
              >
                {currentTitle}
              </h2>
              {isAdmin() && !isEditingDetails && (
                <div className="flex gap-2 ml-3">
                  <button
                    onClick={handleEditDetailsClick}
                    className="p-2 rounded-full hover:bg-gray-700 transition-colors"
                    title="Edit Title & Description"
                    style={{
                      background: "rgba(255, 255, 255, 0.1)",
                      backdropFilter: "blur(10px)",
                      WebkitBackdropFilter: "blur(10px)",
                    }}
                  >
                    <Edit size={16} className="text-white" />
                  </button>
                  <button
                    onClick={handleReplaceTorrentClick}
                    className="p-2 rounded-full hover:bg-gray-700 transition-colors"
                    title="Replace Torrent File"
                    style={{
                      background: "rgba(255, 165, 0, 0.2)",
                      backdropFilter: "blur(10px)",
                      WebkitBackdropFilter: "blur(10px)",
                      border: "1px solid rgba(255, 165, 0, 0.3)",
                    }}
                  >
                    <FileText size={16} className="text-orange-400" />
                  </button>
                  <button
                    onClick={handleArchiveManagerClick}
                    className="p-2 rounded-full hover:bg-gray-700 transition-colors"
                    title="Manage Archive Files"
                    style={{
                      background: "rgba(147, 51, 234, 0.2)",
                      backdropFilter: "blur(10px)",
                      WebkitBackdropFilter: "blur(10px)",
                      border: "1px solid rgba(147, 51, 234, 0.3)",
                    }}
                  >
                    <Archive size={16} className="text-purple-400" />
                  </button>
                </div>
              )}
            </div>

            {/* Stylish Divider Line */}
            <div
              style={{
                height: "1px",
                background:
                  "linear-gradient(90deg, transparent 0%, rgba(255, 255, 255, 0.15) 20%, rgba(128, 128, 255, 0.3) 50%, rgba(255, 255, 255, 0.15) 80%, transparent 100%)",
                marginBottom: "1rem",
                position: "relative",
              }}
            >
              <div
                style={{
                  position: "absolute",
                  top: "-1px",
                  left: "50%",
                  transform: "translateX(-50%)",
                  width: "50%",
                  height: "3px",
                  background:
                    "linear-gradient(90deg, transparent 0%, rgba(128, 128, 255, 0.4) 50%, transparent 100%)",
                  filter: "blur(1px)",
                }}
              />
            </div>
            {!isEditingDetails ? (
              <div
                className="mb-4 leading-relaxed"
                style={{
                  color: "#999",
                  wordBreak: "break-word",
                  overflowWrap: "anywhere",
                }}
                dangerouslySetInnerHTML={{
                  __html: currentDescription.replace(
                    /<a /g,
                    '<a style="color:#6666ff;word-break:break-word;overflow-wrap:anywhere;" ',
                  ),
                }}
              />
            ) : (
              <div className="mb-4">
                <DetailsEditor
                  torrentId={item.id}
                  currentTitle={currentTitle}
                  currentDescription={currentDescription}
                  currentTime={currentTime}
                  currentCategory={currentCategory}
                  currentSize={currentSize}
                  onSave={handleDetailsSave}
                  onCancel={handleDetailsCancelEdit}
                />
              </div>
            )}
          </div>
          {hasTorrent && (isAdmin() || !isBanned()) && (
            <TorrentFileTree
              torrentId={item.id}
              refreshKey={`${fileTreeRefreshKey}:${hasAccess()}`}
              onLockedClick={() => setShowAccessDenied(true)}
            />
          )}
          {/* Comments Section */}
          <div
            className="p-6 m-4 rounded-xl"
            style={{
              backgroundColor: "#1a1a20",
              border: "1px solid rgba(255, 255, 255, 0.05)",
            }}
          >
            <h3
              className="text-xl font-semibold mb-4"
              style={{ color: "#999" }}
            >
              Comments
            </h3>
            <CommentsSection torrentId={item.id} />
          </div>
        </div>

        <FloatingDownloadBar
          onDownloadTorrent={handleDownload}
          onDownloadArchive={handleDownloadArchive}
          hasTorrent={hasTorrent}
          hasArchive={hasArchive}
        />

        {/* Back to Top Button for Modal */}
        <BackToTopButton
          targetElement={scrollTarget}
          isModal={true}
        />
      </div>

      {/* Модальные окна */}
      <PremiumModalContainer
        isOpen={showAccessDenied}
        onClose={() => setShowAccessDenied(false)}
      />
      <ServiceUnavailableModal
        isOpen={showServiceUnavailable}
        onClose={() => setShowServiceUnavailable(false)}
      />
      <ReplaceTorrentModal
        isOpen={showReplaceTorrent}
        onClose={() => setShowReplaceTorrent(false)}
        torrent={item}
        onSuccess={handleReplaceTorrentSuccess}
      />
      <ArchiveManagerModal
        isOpen={showArchiveManager}
        onClose={() => setShowArchiveManager(false)}
        torrentId={item.id}
        torrentTitle={item.title}
      />
    </div>
  );
};

// --- ContentCard с size и time ---
function formatTimeAgo(timeString) {
  if (!timeString) return "";
  // Пример: "May 04 2025, 05:34"
  try {
    // Парсим время как UTC, чтобы избежать смещения временной зоны
    const parsed = Date.parse(
      timeString.replace(/(\d{2}) (\d{4}),/, "$1, $2") + " UTC",
    );
    const date = isNaN(parsed)
      ? new Date(timeString + " UTC")
      : new Date(parsed);
    if (isNaN(date.getTime())) return "";

    // Используем обычное текущее время, так как date уже в UTC
    const now = new Date();
    const diff = Math.floor((now - date) / 1000); // в секундах

    if (diff < 60) return "just now";
    if (diff < 3600)
      return `${Math.floor(diff / 60)} min${
        Math.floor(diff / 60) === 1 ? "" : "s"
      } ago`;
    if (diff < 86400)
      return `${Math.floor(diff / 3600)} hour${
        Math.floor(diff / 3600) === 1 ? "" : "s"
      } ago`;
    if (diff < 604800)
      return `${Math.floor(diff / 86400)} day${
        Math.floor(diff / 86400) === 1 ? "" : "s"
      } ago`;
    if (diff < 2592000)
      return `${Math.floor(diff / 604800)} week${
        Math.floor(diff / 604800) === 1 ? "" : "s"
      } ago`;
    if (diff < 31536000)
      return `${Math.floor(diff / 2592000)} month${
        Math.floor(diff / 2592000) === 1 ? "" : "s"
      } ago`;
    return `${Math.floor(diff / 31536000)} year${
      Math.floor(diff / 31536000) === 1 ? "" : "s"
    } ago`;
  } catch (error) {
    console.error("Error parsing date:", timeString, error);
    return "";
  }
}

const CARD_WIDTH = 640; // px, ширина для 16:9
const CARD_HEIGHT = 504; // px, высота увеличена на 40% (360 * 1.4 = 504)
const CARD_GAP = 16; // px, gap между карточками
const CARD_FOOTER_HEIGHT = 108; // px, тёмная подпись не растёт вместе с шириной
const BREAKPOINT = 1000; // px, breakpoint for determining mobile vs desktop layout
const VISIBLE_COUNT = 8; // сколько карточек примерно видно на экране (для оптимизации)

const ContentCard = ({
  torrentId,
  img_url,
  image_urls = [],
  title,
  size,
  time,
  category,
  is_exclusive,
  is_top,
  visible_to_banned,
  onClick,
  onDelete,
  onBookmarkClick,
  onTopClick,
  isFavorite = false,
}) => {
  // Определяем, мобильное устройство или десктоп
  const [isMobile, setIsMobile] = React.useState(
    window.innerWidth < BREAKPOINT,
  );
  const [isPhone, setIsPhone] = React.useState(window.innerWidth <= 700);
  const [isEditing, setIsEditing] = React.useState(false);
  const [currentImageUrls, setCurrentImageUrls] = React.useState(
    getTorrentImageUrls({ image_url: img_url, image_urls }),
  );
  const [currentIsExclusive, setCurrentIsExclusive] =
    React.useState(is_exclusive);
  const [currentVisibleToBanned, setCurrentVisibleToBanned] = React.useState(
    visible_to_banned !== false,
  );
  const { isAdmin } = useUser();

  React.useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < BREAKPOINT);
      setIsPhone(window.innerWidth <= 700);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  // Обновляем локальную галерею при изменении пропсов
  React.useEffect(() => {
    setCurrentImageUrls(
      getTorrentImageUrls({ image_url: img_url, image_urls }),
    );
  }, [img_url, image_urls]);

  // Обновляем локальный exclusive статус при изменении пропса
  React.useEffect(() => {
    setCurrentIsExclusive(is_exclusive);
  }, [is_exclusive]);

  // Обновляем локальный visible_to_banned статус при изменении пропса
  React.useEffect(() => {
    setCurrentVisibleToBanned(visible_to_banned !== false);
  }, [visible_to_banned]);

  const currentImageUrl = getPrimaryTorrentImageUrl({
    image_url: img_url,
    image_urls: currentImageUrls,
  });

  const handleEditClick = (e) => {
    e.stopPropagation();
    setIsEditing(true);
  };

  const handleDeleteClick = (e) => {
    e.stopPropagation();
    if (
      window.confirm(
        `Are you sure you want to delete "${title}"?\n\nThis action cannot be undone.`,
      )
    ) {
      onDelete && onDelete(torrentId);
    }
  };

  const handleToggleExclusive = async (e) => {
    e.stopPropagation();
    try {
      const response = await fetch(
        `/api/admin/torrents/${torrentId}/toggle-exclusive`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `tma ${window.Telegram.WebApp.initData}`,
          },
        },
      );

      if (response.ok) {
        const result = await response.json();
        setCurrentIsExclusive(result.is_exclusive);
      } else {
        console.error("Failed to toggle exclusive status");
      }
    } catch (error) {
      console.error("Error toggling exclusive status:", error);
    }
  };

  const handleToggleBannedVisibility = async (e) => {
    e.stopPropagation();
    try {
      const response = await fetch(
        `/api/admin/torrents/${torrentId}/toggle-banned-visibility`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `tma ${window.Telegram.WebApp.initData}`,
          },
        },
      );

      if (response.ok) {
        const result = await response.json();
        setCurrentVisibleToBanned(result.visible_to_banned);
      } else {
        console.error("Failed to toggle banned visibility");
      }
    } catch (error) {
      console.error("Error toggling banned visibility:", error);
    }
  };

  const handleSaveImage = (newImageUrl, newImageUrls) => {
    setCurrentImageUrls(
      getTorrentImageUrls({ image_url: newImageUrl, image_urls: newImageUrls }),
    );
    setIsEditing(false);
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
  };

  return (
    <div className="relative mb-4" style={{ width: "100%" }}>
      {/* Bookmark для добавления в избранное */}
      <button
        className={`ui-bookmark absolute -top-1 right-4 z-40${isFavorite ? " active" : ""}`}
        onClick={(e) => {
          e.stopPropagation();
          onBookmarkClick && onBookmarkClick();
        }}
      >
        <div className="bookmark">
          <svg viewBox="0 0 32 32">
            <path d="M27 4v27a1 1 0 0 1-1.625.781L16 24.281l-9.375 7.5A1 1 0 0 1 5 31V4a4 4 0 0 1 4-4h14a4 4 0 0 1 4 4z"></path>
          </svg>
        </div>
      </button>

      <div
        onClick={onClick}
        className={`rounded-xl shadow-lg overflow-hidden cursor-pointer w-full border content-card${
          currentIsExclusive ? " exclusive" : ""
        }`}
        style={{
          backgroundColor: "#303030",
          borderColor: "#282828",
          width: "100%",
          position: "relative",
          boxShadow: "0 4px 24px 0 rgba(0,0,0,0.18)",
        }}
      >
        {/* Картинка 16:9. Тёмная подпись снизу фиксированной высоты. */}
        <div
          style={{
            width: "100%",
            paddingBottom: "56.25%",
            position: "relative",
          }}
        >
          <div
            className="absolute inset-0 w-full h-full"
            style={{
              width: "100%",
              height: "100%",
              position: "absolute",
              left: 0,
              top: 0,
            }}
          >
            {/* Эксклюзивная лента для эксклюзивных торрентов */}
            {currentIsExclusive && (
              <div className="exclusive-ribbon">
                <div className="ribbon-track">
                  <span className="ribbon-text">{"HOT DROP • HOT DROP • HOT DROP • "}</span>
                  <span className="ribbon-text" aria-hidden="true">{"HOT DROP • HOT DROP • HOT DROP • "}</span>
                </div>
              </div>
            )}

            {/* Горизонтальная панель админских кнопок сверху по центру */}
            {isAdmin() && !isEditing && (
              <div
                className="absolute top-3 left-1/2 z-10 flex items-center gap-2"
                style={{
                  transform: "translateX(-50%)",
                }}
              >
                {/* Edit Image */}
                <button
                  onClick={handleEditClick}
                  className="p-2 bg-blue-600/80 hover:bg-blue-700/90 backdrop-blur-sm border border-blue-500/50 rounded-full transition-all duration-200"
                  title="Edit Image"
                  style={{
                    backdropFilter: "blur(8px)",
                    WebkitBackdropFilter: "blur(8px)",
                  }}
                >
                  <Edit size={14} className="text-white" />
                </button>

                {/* Delete Torrent */}
                <button
                  onClick={handleDeleteClick}
                  className="p-2 bg-red-600/80 hover:bg-red-700/90 backdrop-blur-sm border border-red-500/50 rounded-full transition-all duration-200"
                  title="Delete Torrent"
                  style={{
                    backdropFilter: "blur(8px)",
                    WebkitBackdropFilter: "blur(8px)",
                  }}
                >
                  <Trash2 size={14} className="text-white" />
                </button>

                {/* Mark as Exclusive */}
                <button
                  onClick={handleToggleExclusive}
                  className={`p-2 backdrop-blur-sm border rounded-full transition-all duration-200 ${
                    currentIsExclusive
                      ? "bg-yellow-500/80 hover:bg-yellow-600/90 border-yellow-400/50"
                      : "bg-gray-600/80 hover:bg-gray-700/90 border-gray-500/50"
                  }`}
                  title={
                    currentIsExclusive
                      ? "Remove Exclusive Status"
                      : "Mark as Exclusive"
                  }
                  style={{
                    backdropFilter: "blur(8px)",
                    WebkitBackdropFilter: "blur(8px)",
                  }}
                >
                  <Star
                    size={14}
                    className={`transition-colors ${
                      currentIsExclusive ? "text-yellow-100" : "text-gray-300"
                    }`}
                    fill={currentIsExclusive ? "currentColor" : "none"}
                  />
                </button>

                {/* Add to TOP */}
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onTopClick && onTopClick();
                  }}
                  className={`p-2 backdrop-blur-sm border rounded-full transition-all duration-200 ${
                    is_top
                      ? "bg-yellow-500/80 hover:bg-yellow-600/90 border-yellow-400/50"
                      : "bg-gray-600/80 hover:bg-gray-700/90 border-gray-500/50"
                  }`}
                  title={is_top ? "Manage TOP position" : "Add to TOP-10"}
                  style={{
                    backdropFilter: "blur(8px)",
                    WebkitBackdropFilter: "blur(8px)",
                  }}
                >
                  <Trophy
                    size={14}
                    className={`transition-colors ${
                      is_top ? "text-yellow-100" : "text-gray-300"
                    }`}
                    fill={is_top ? "currentColor" : "none"}
                  />
                </button>

                {/* Видимость для забаненных — checkbox */}
                <label
                  className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-full backdrop-blur-sm border transition-all duration-200 cursor-pointer ${
                    currentVisibleToBanned
                      ? "bg-green-600/80 hover:bg-green-700/90 border-green-500/50"
                      : "bg-red-500/80 hover:bg-red-600/90 border-red-400/50"
                  }`}
                  title={
                    currentVisibleToBanned
                      ? "Visible to banned users (click to hide)"
                      : "Hidden from banned users (click to show)"
                  }
                  style={{
                    backdropFilter: "blur(8px)",
                    WebkitBackdropFilter: "blur(8px)",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={currentVisibleToBanned}
                    onChange={handleToggleBannedVisibility}
                    className="sr-only"
                  />
                  {currentVisibleToBanned ? (
                    <Eye
                      size={14}
                      className="transition-colors text-green-100"
                    />
                  ) : (
                    <EyeOff
                      size={14}
                      className="transition-colors text-red-100"
                    />
                  )}
                  <span
                    className="text-white text-xs font-medium whitespace-nowrap"
                    style={{ textShadow: "0 1px 2px rgba(0,0,0,0.5)" }}
                  >
                    Ban
                  </span>
                </label>
              </div>
            )}

            {/* Компонент редактирования изображения */}
            {isEditing && (
              <ImageEditor
                torrentId={torrentId}
                currentImageUrl={currentImageUrl}
                currentImageUrls={currentImageUrls}
                onSave={handleSaveImage}
                onCancel={handleCancelEdit}
              />
            )}

            <div
              style={{
                width: "100%",
                height: "100%",
                position: "absolute",
                left: 0,
                top: 0,
              }}
            >
              <TorrentImageGallery
                images={currentImageUrls}
                alt={title}
                className="w-full h-full"
                imageClassName="w-full h-full object-cover opacity-90 transition-opacity duration-300 hover:opacity-100"
                imageStyle={{ height: "100%", objectFit: "cover" }}
                dotContainerStyle={{ bottom: isPhone ? 8 : 16 }}
              />
            </div>
            <div
              className="absolute left-0 bottom-0 w-full"
              style={{
                height: 64,
                background:
                  "linear-gradient(0deg, rgba(24,24,24,0.99) 0%, rgba(24,24,24,0.72) 46%, rgba(24,24,24,0) 100%)",
                pointerEvents: "none",
                zIndex: 2,
              }}
            />
          </div>
        </div>
        <div
          className="px-4 flex flex-col justify-end z-10"
          style={{
            height: CARD_FOOTER_HEIGHT,
            boxSizing: "border-box",
            paddingTop: 0,
            paddingBottom: 22,
            background: "#181818",
            pointerEvents: "none",
          }}
        >
              {/* Title - Moved up */}
              <div
                className="mb-2"
                style={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  display: "-webkit-box",
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: "vertical",
                  padding: "0 0.2rem",
                  maxWidth: "100%",
                  width: "fit-content",
                }}
              >
                <span
                  className="font-bold"
                  style={{
                    color: "#fff",
                    textShadow:
                      "0 2px 10px rgba(0,0,0,0.8), 0 0 2px rgba(0,0,0,0.5)",
                    fontSize: isMobile
                      ? `clamp(1.1rem, 4vw, 1.3rem)`
                      : "1.3rem",
                    lineHeight: "1.2",
                    letterSpacing: "-0.01em",
                  }}
                >
                  {title}
                </span>
              </div>

              {/* Single line meta: Category • Time • Size */}
              <div
                className="flex items-center w-full px-1"
                style={{
                  color: "rgba(255, 255, 255, 0.7)",
                  fontSize: isMobile ? "0.85rem" : "0.95rem",
                  fontWeight: "500",
                  letterSpacing: "0.02em",
                  textShadow: "0 1px 4px rgba(0,0,0,0.8)",
                  fontFamily:
                    "SF Pro Display, -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, sans-serif",
                }}
              >
                <span>{category}</span>
                <span className="mx-2 opacity-60">•</span>
                <span>{formatTimeAgo(time)}</span>
                <span className="mx-2 opacity-60">•</span>
                <span>{size}</span>
              </div>
        </div>
      </div>
    </div>
  );
};

function VirtualizedCardList({
  items,
  onCardClick,
  onCardDelete,
  onBookmarkClick,
  onTopClick,
  favoriteIds = new Set(),
}) {
  const [isMobile, setIsMobile] = React.useState(
    window.innerWidth < BREAKPOINT,
  );

  React.useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < BREAKPOINT);
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const columnCount = isMobile ? 1 : 3;
  const rowCount = Math.ceil(items.length / columnCount);
  const listRef = React.useRef();

  return (
    <div
      style={{
        width: "100%",
        maxWidth: isMobile ? CARD_WIDTH : 1200,
        margin: "0 auto",
        flex: 1,
      }}
    >
      <AutoSizer disableHeight>
        {({ width }) => {
          // For mobile, itemWidth is full width (capped by CARD_WIDTH above).
          // For desktop, it's (width - totalGap) / 3
          const gapTotal = isMobile ? 0 : (columnCount - 1) * CARD_GAP;
          const itemWidth = isMobile
            ? width
            : Math.floor((width - gapTotal) / columnCount);

          // Картинка остаётся 16:9, тёмная подпись не зависит от ширины.
          const referenceWidth = isMobile
            ? Math.min(width, CARD_WIDTH)
            : itemWidth;
          const cardHeight =
            Math.floor(referenceWidth * (9 / 16)) + CARD_FOOTER_HEIGHT;

          return (
            <List
              ref={listRef}
              height={rowCount * (cardHeight + CARD_GAP)}
              itemCount={rowCount}
              itemSize={cardHeight + CARD_GAP}
              width={width}
              outerElementType="div"
              style={{ overflow: "visible" }}
            >
              {({ index, style }) => {
                const startIndex = index * columnCount;
                const rowItems = items.slice(
                  startIndex,
                  startIndex + columnCount,
                );

                return (
                  <div
                    style={{
                      ...style,
                      width: "100%",
                      display: "flex",
                      justifyContent: isMobile ? "center" : "flex-start",
                      gap: isMobile ? 0 : CARD_GAP,
                    }}
                  >
                    {rowItems.map((item) => (
                      <div
                        key={item.id}
                        style={{
                          width: itemWidth,
                          display: "flex",
                          justifyContent: "center",
                        }}
                      >
                        <ContentCard
                          isFavorite={favoriteIds.has(Number(item.id))}
                          torrentId={item.id}
                          img_url={item.image_url}
                          image_urls={item.image_urls}
                          title={item.title}
                          size={item.size}
                          time={item.time}
                          category={item.category}
                          is_exclusive={item.is_exclusive}
                          is_top={item.is_top}
                          visible_to_banned={item.visible_to_banned}
                          onClick={() => onCardClick(item)}
                          onDelete={(torrentId) =>
                            onCardDelete && onCardDelete(torrentId)
                          }
                          onBookmarkClick={() =>
                            onBookmarkClick && onBookmarkClick(item)
                          }
                          onTopClick={() => onTopClick && onTopClick(item)}
                        />
                      </div>
                    ))}
                  </div>
                );
              }}
            </List>
          );
        }}
      </AutoSizer>
    </div>
  );
}

// Filter Menu Component
const FilterMenu = ({
  selectedCategories,
  onCategoryToggle,
  visible,
  onClose,
  isGlobalSearch = false,
  selectedTrackers = [],
  onTrackerToggle = null,
}) => {
  if (!visible) return null;

  const handleCategoryClick = (categoryId) => {
    onCategoryToggle(categoryId);
  };

  const handleCategoryTouch = (e, categoryId) => {
    e.preventDefault();
    e.stopPropagation();
    onCategoryToggle(categoryId);
  };

  const handleTrackerClick = (trackerId) => {
    if (onTrackerToggle) {
      onTrackerToggle(trackerId);
    }
  };

  const SUPPORTED_TRACKERS = [
    { id: "rutracker", label: "RuTracker", color: "#ff6b35" },
    { id: "nnmclub", label: "NNM-Club", color: "#4ecdc4" },
    { id: "piratebay", label: "PirateBay", color: "#45b7d1" },
    { id: "1337x", label: "1337x", color: "#96ceb4" },
    { id: "rarbg", label: "RARBG", color: "#feca57" },
  ];

  const itemsToShow = isGlobalSearch ? SUPPORTED_TRACKERS : CATEGORIES;
  const selectedItems = isGlobalSearch ? selectedTrackers : selectedCategories;

  return (
    <div
      className="absolute left-0 top-full z-50 transition-all duration-300 animate-in neon-filter-menu"
      onClick={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      {/* Particle effects */}
      <div className="filter-particles">
        <div className="filter-particle"></div>
        <div className="filter-particle"></div>
        <div className="filter-particle"></div>
        <div className="filter-particle"></div>
        <div className="filter-particle"></div>
      </div>

      {/* Glow effects */}
      <div className="filter-glow-left"></div>
      <div className="filter-glow-right"></div>

      {/* Filter content */}
      <div className="flex flex-wrap gap-3 justify-center p-4">
        {itemsToShow.map((item) => {
          const isSelected = selectedItems.includes(item.id);

          return (
            <button
              key={item.id}
              onClick={() =>
                isGlobalSearch
                  ? handleTrackerClick(item.id)
                  : handleCategoryClick(item.id)
              }
              onTouchEnd={(e) => {
                e.preventDefault();
                e.stopPropagation();
                isGlobalSearch
                  ? handleTrackerClick(item.id)
                  : handleCategoryClick(item.id);
              }}
              className={`neon-filter-chip ${isSelected ? "active" : ""} ${item.id === "exclusive" ? "exclusive-chip" : ""}`}
              style={{
                ...(isGlobalSearch && isSelected
                  ? {
                      borderColor: item.color,
                      boxShadow: `0 0 10px ${item.color}40`,
                    }
                  : {}),
                ...(item.id === "exclusive" && !isSelected
                  ? {
                      background:
                        "linear-gradient(135deg, rgba(230, 180, 0, 0.15) 0%, rgba(200, 120, 0, 0.05) 100%)",
                      borderColor: "rgba(255, 215, 0, 0.5)",
                      color: "#ffd700",
                      boxShadow: "0 0 8px rgba(255, 215, 0, 0.1)",
                    }
                  : {}),
                ...(item.id === "exclusive" && isSelected
                  ? {
                      background:
                        "linear-gradient(135deg, #FFD700 0%, #FFA500 100%)",
                      borderColor: "#FFF",
                      color: "#000",
                      fontWeight: "700",
                      boxShadow: "0 0 16px rgba(255, 215, 0, 0.6)",
                      textShadow: "none",
                    }
                  : {}),
              }}
            >
              {isGlobalSearch && (
                <span
                  className="inline-block w-3 h-3 rounded-full mr-2"
                  style={{ backgroundColor: isSelected ? item.color : "#666" }}
                />
              )}
              {item.label}
              {isSelected && (
                <span className="inline-block ml-2 opacity-90">✓</span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Search Bar Component
const SearchBar = ({
  onSearch,
  onCategoryChange,
  selectedCategories = [],
  searchQuery = "",
  totalCount = null,
  onShowAccessDenied,
  isGlobalSearch = false,
  setIsGlobalSearch = null,
  isAdmin = null,
  selectedTrackers = [],
  onTrackerChange = null,
  searchAction = "filter",
  onGlobalSearchVisibilityChange = null,
}) => {
  const [filterVisible, setFilterVisible] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [dbSearchEnabledForAll, setDbSearchEnabledForAll] = useState(false);
  const filterRef = useRef(null);
  const inputRef = useRef(null);
  const { hasAccess } = useUser();

  // Управляемое состояние для поля поиска
  const [inputValue, setInputValue] = useState(searchQuery);
  const isInitialized = useRef(false);

  // Инициализируем inputValue только один раз при монтировании или если searchQuery очищается извне
  useEffect(() => {
    if (!isInitialized.current || searchQuery === "") {
      setInputValue(searchQuery);
      isInitialized.current = true;
    }
  }, [searchQuery]);

  useEffect(() => {
    let isCancelled = false;

    const loadDatabaseSearchSetting = async () => {
      try {
        const response = await fetch(`/api/users/settings/database-search`);
        const data = await response.json();

        if (!isCancelled && data.success) {
          setDbSearchEnabledForAll(Boolean(data.enabledForAll));
        }
      } catch (error) {
        console.error("Error loading database search setting:", error);
        if (!isCancelled) {
          setDbSearchEnabledForAll(false);
        }
      }
    };

    loadDatabaseSearchSetting();

    return () => {
      isCancelled = true;
    };
  }, []);

  const canUseRegularSearch =
    dbSearchEnabledForAll || hasAccess() || Boolean(isAdmin);
  const canUseGlobalSearch = hasAccess() || Boolean(isAdmin);
  const canUseCurrentSearch = isGlobalSearch
    ? canUseGlobalSearch
    : canUseRegularSearch;

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        setTimeout(() => {
          setFilterVisible(false);
        }, 100);
      }
    };
    const handleScroll = () => {
      if (
        isFocused &&
        inputRef.current &&
        document.activeElement === inputRef.current
      ) {
        inputRef.current.blur();
        setIsFocused(false);
      }
      if (filterVisible) {
        setFilterVisible(false);
      }
    };
    const handleTouchStart = (event) => {
      if (filterRef.current && !filterRef.current.contains(event.target)) {
        if (isFocused && inputRef.current) {
          inputRef.current.blur();
          setIsFocused(false);
        }
        setFilterVisible(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("touchstart", handleTouchStart, {
      passive: true,
    });
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("touchstart", handleTouchStart);
      window.removeEventListener("scroll", handleScroll);
    };
  }, [filterVisible, isFocused]);

  useEffect(() => {
    if (searchAction === "download") {
      setFilterVisible(false);
    }
  }, [searchAction]);

  const handleCategoryToggle = (categoryId) => {
    if (!canUseRegularSearch) {
      onShowAccessDenied();
      return;
    }

    const newCategories = selectedCategories.includes(categoryId)
      ? selectedCategories.filter((id) => id !== categoryId)
      : [...selectedCategories, categoryId];
    onCategoryChange(newCategories);
  };

  const handleTrackerToggle = (trackerId) => {
    if (!canUseGlobalSearch) {
      onShowAccessDenied();
      return;
    }

    if (onTrackerChange) {
      const updatedTrackers = selectedTrackers.includes(trackerId)
        ? selectedTrackers.filter((id) => id !== trackerId)
        : [...selectedTrackers, trackerId];

      onTrackerChange(updatedTrackers);
    }
  };

  const handleInputFocus = () => {
    if (!canUseCurrentSearch) {
      onShowAccessDenied();
      if (inputRef.current) {
        inputRef.current.blur();
      }
      return;
    }
    setIsFocused(true);
  };

  const handleInputBlur = () => {
    setIsFocused(false);
  };

  const handleFilterToggle = (e) => {
    e.preventDefault();
    e.stopPropagation();

    if (searchAction === "download") {
      return;
    }

    if (!canUseCurrentSearch) {
      onShowAccessDenied();
      return;
    }

    setFilterVisible(!filterVisible);
  };

  // Обработчик изменения поля поиска
  const handleInputChange = (e) => {
    if (!canUseCurrentSearch) {
      onShowAccessDenied();
      e.target.blur();
      return;
    }

    const newValue = e.target.value;
    setInputValue(newValue);

    // Если поле очистили (удалили весь текст), автоматически сбрасываем поиск
    if (newValue === "" && searchQuery !== "") {
      onSearch("");
    }
  };

  // Обработчик нажатия Enter для поиска
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleSearchSubmit();
    }
  };

  // Обработчик кнопки поиска
  const handleSearchSubmit = () => {
    if (!canUseCurrentSearch) {
      onShowAccessDenied();
      return;
    }
    // Скрываем клавиатуру на мобильных
    if (inputRef.current) {
      inputRef.current.blur();
    }
    onSearch(inputValue);
  };

  // Обработчик очистки поля поиска
  const handleClear = () => {
    if (!canUseCurrentSearch) {
      onShowAccessDenied();
      return;
    }

    setInputValue("");
    // Сбрасываем поиск только если он был ранее активирован (searchQuery не пустой)
    if (searchQuery) {
      onSearch("");
    }
    if (inputRef.current) inputRef.current.focus();
  };

  return (
    <div
      className="relative w-full"
      ref={filterRef}
      style={{ maxWidth: "640px", margin: "0 auto" }}
    >
      <div
        className={`neon-search-container${
          searchAction === "download" ? " is-paste" : ""
        }`}
      >
        <div className="particle-container">
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
          <div className="particle"></div>
        </div>
        <div className="glow-container">
          <div className="glow-effect-left"></div>
          <div className="glow-effect-right"></div>
          <div
            className={`search-bar${
              searchAction === "download" ? " is-paste" : ""
            }${inputValue ? " has-value" : ""}`}
          >
            <div className="search-icon" aria-hidden="true">
              {searchAction === "download" ? (
                <svg
                  className="link-icon"
                  xmlns="http://www.w3.org/2000/svg"
                  width="20"
                  height="20"
                  viewBox="0 0 20 20"
                >
                  <path
                    fill="#ffffff"
                    d="M17.74 2.76a4.32 4.32 0 0 1 0 6.1l-1.53 1.52c-1.12 1.12-2.7 1.47-4.14 1.09l2.62-2.61l.76-.77l.76-.76c.84-.84.84-2.2 0-3.04a2.13 2.13 0 0 0-3.04 0l-.77.76l-3.38 3.38c-.37-1.44-.02-3.02 1.1-4.14l1.52-1.53a4.32 4.32 0 0 1 6.1 0M8.59 13.43l5.34-5.34c.42-.42.42-1.1 0-1.52c-.44-.43-1.13-.39-1.53 0l-5.33 5.34c-.42.42-.42 1.1 0 1.52c.44.43 1.13.39 1.52 0m-.76 2.29l4.14-4.15c.38 1.44.03 3.02-1.09 4.14l-1.52 1.53a4.32 4.32 0 0 1-6.1 0a4.32 4.32 0 0 1 0-6.1l1.53-1.52c1.12-1.12 2.7-1.47 4.14-1.1l-4.14 4.15c-.85.84-.85 2.2 0 3.05c.84.84 2.2.84 3.04 0"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                  ></path>
                </svg>
              )}
            </div>
            <input
              ref={inputRef}
              type="text"
              className="search-input"
              placeholder={
                searchAction === "download"
                  ? "Paste a link"
                  : isGlobalSearch
                    ? "Search globally..."
                    : totalCount !== null
                      ? `Search... (Total: ${totalCount} torrents)`
                      : "Search..."
              }
              value={inputValue}
              onChange={handleInputChange}
              onFocus={handleInputFocus}
              onBlur={handleInputBlur}
              onKeyPress={handleKeyPress}
              style={{
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                overflow: "hidden",
                paddingRight:
                  searchAction === "download"
                    ? inputValue
                      ? "86px"
                      : "54px"
                    : inputValue
                      ? "115px"
                      : "54px",
                transition: "padding-right 0.2s ease",
              }}
            />

            {/* Кнопка поиска. В Paste a link отправка уже на RIP ME. */}
            {inputValue && searchAction !== "download" && (
              <button
                type="button"
                className="clear-search-btn"
                onClick={handleSearchSubmit}
                aria-label="Search"
                tabIndex={0}
                style={{
                  right: "80px",
                  backgroundColor: "transparent",
                  zIndex: 2,
                }}
              >
                <svg
                  width="24"
                  height="24"
                  viewBox="0 0 48 48"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M8,9.7a2,2,0,0,1,.6-1.4A21.6,21.6,0,0,1,24,2a22,22,0,0,1,0,44A21.6,21.6,0,0,1,8.6,39.7a2,2,0,1,1,2.8-2.8,18,18,0,1,0,0-25.8,1.9,1.9,0,0,1-2.8,0A2,2,0,0,1,8,9.7Z"
                    fill="#03e9f4"
                  />
                  <path
                    d="M33.4,22.6l-7.9-8a2.1,2.1,0,0,0-2.7-.2,1.9,1.9,0,0,0-.2,3L27.2,22H4a2,2,0,0,0-2,2H2a2,2,0,0,0,2,2H27.2l-4.6,4.6a1.9,1.9,0,0,0,.2,3,2.1,2.1,0,0,0,2.7-.2l7.9-8A1.9,1.9,0,0,0,33.4,22.6Z"
                    fill="#03e9f4"
                  />
                </svg>
              </button>
            )}

            {/* Крестик-очистка */}
            {inputValue && (
              <button
                type="button"
                className="clear-search-btn"
                onClick={handleClear}
                aria-label="Clear search"
                tabIndex={0}
                style={{
                  right: "50px",
                  zIndex: 2,
                }}
              >
                <svg
                  width="18"
                  height="18"
                  viewBox="0 0 20 20"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <circle
                    cx="10"
                    cy="10"
                    r="9"
                    stroke="#888"
                    strokeWidth="2"
                    fill="#232323"
                  />
                  <path
                    d="M7 7L13 13"
                    stroke="#aaa"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                  <path
                    d="M13 7L7 13"
                    stroke="#aaa"
                    strokeWidth="2"
                    strokeLinecap="round"
                  />
                </svg>
              </button>
            )}

            {searchAction === "download" ? (
              <button
                type="button"
                className="rip-me-btn"
                onClick={handleSearchSubmit}
                aria-label="Download"
              >
                <span className="rip-me-sign" aria-hidden="true">
                  <svg viewBox="0 0 512 512">
                    <path d="M377.9 105.9L500.7 228.7c7.2 7.2 11.3 17.1 11.3 27.3s-4.1 20.1-11.3 27.3L377.9 406.1c-6.4 6.4-15 9.9-24 9.9c-18.7 0-33.9-15.2-33.9-33.9l0-62.1-128 0c-17.7 0-32-14.3-32-32l0-64c0-17.7 14.3-32 32-32l128 0 0-62.1c0-18.7 15.2-33.9 33.9-33.9c9 0 17.6 3.6 24 9.9zM160 96L96 96c-17.7 0-32 14.3-32 32l0 256c0 17.7 14.3 32 32 32l64 0c17.7 0 32 14.3 32 32s-14.3 32-32 32l-64 0c-53 0-96-43-96-96L0 128C0 75 43 32 96 32l64 0c17.7 0 32 14.3 32 32s-14.3 32-32 32z" />
                  </svg>
                </span>
                <span className="rip-me-text">RIP ME</span>
              </button>
            ) : (
              <button
                className="filter-container"
                onClick={handleFilterToggle}
                onTouchEnd={handleFilterToggle}
                aria-label="Filter"
                style={{
                  zIndex: 2,
                  background: filterVisible
                    ? "linear-gradient(135deg, rgba(120, 100, 255, 0.4) 0%, rgba(120, 100, 255, 0.2) 100%)"
                    : "linear-gradient(135deg, rgba(120, 100, 255, 0.2) 0%, rgba(120, 100, 255, 0.1) 100%)",
                }}
              >
                <div className="filter-icon">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z"
                    ></path>
                  </svg>
                </div>
                {selectedCategories.length > 0 && (
                  <span
                    className="absolute -top-1 -right-1 w-5 h-5 rounded-full text-xs flex items-center justify-center"
                    style={{ backgroundColor: "#282828", color: "#fff" }}
                  >
                    {selectedCategories.length}
                  </span>
                )}
              </button>
            )}
          </div>
        </div>
        {searchAction === "download" && <SourceMarquee />}
      </div>

      {searchAction !== "download" && (
        <CyberToggle
          isGlobalSearch={isGlobalSearch}
          setIsGlobalSearch={setIsGlobalSearch}
          isAdmin={isAdmin}
          canUseGlobalSearch={canUseGlobalSearch}
          onVisibilityChange={onGlobalSearchVisibilityChange}
        />
      )}

      {/* Animated filter menu with horizontal categories */}
      <FilterMenu
        visible={filterVisible}
        selectedCategories={selectedCategories}
        onCategoryToggle={handleCategoryToggle}
        onClose={() => setFilterVisible(false)}
        isGlobalSearch={isGlobalSearch}
        selectedTrackers={selectedTrackers}
        onTrackerToggle={handleTrackerToggle}
      />
    </div>
  );
};

// Loading Spinner Component
const LoadingSpinner = () => (
  <div className="flex justify-center items-center py-4">
    <div
      className="animate-spin rounded-full h-8 w-8 border-2"
      style={{
        borderColor: "#282828",
        borderTopColor: "#999",
      }}
    />
  </div>
);

// Toast компонент для уведомлений
const Toast = ({ message, type = "info", onClose }) => (
  <div
    className={`fixed bottom-4 right-4 px-6 py-3 rounded-lg shadow-lg ${
      type === "error" ? "bg-red-500" : "bg-green-500"
    } text-white`}
  >
    {message}
    <button onClick={onClose} className="ml-3 text-white hover:text-gray-200">
      ×
    </button>
  </div>
);

// --- Компонент аватара Telegram ---
function Avatar({ user, onClick, isClickable }) {
  if (!user) return null;
  const photoUrl = user.photo_url;

  return (
    <div
      className={`relative flex items-center justify-center mr-4 ml-2 rounded-full active:scale-95 transition-transform ${
        isClickable ? "cursor-pointer" : ""
      }`}
      onClick={(e) => {
        if (isClickable && onClick) {
          e.preventDefault();
          e.stopPropagation();
          onClick();
        }
      }}
      style={{ minWidth: 48, minHeight: 48, width: 48, height: 48 }}
    >
      {/* Glow Effect for clickable avatars */}
      {isClickable && (
        <>
          <div
            className="absolute inset-[-5px] rounded-full z-[-1]"
            style={{
              background: `
                radial-gradient(circle at 50% 0%, hsl(27deg 93% 60%), transparent 70%),
                radial-gradient(circle at 100% 50%, #00a6ff, transparent 70%),
                radial-gradient(circle at 50% 100%, #ff0056, transparent 70%),
                radial-gradient(circle at 0% 50%, #6500ff, transparent 70%)
              `,
              opacity: 0.6,
              filter: "blur(4px)",
            }}
          />
          <div
            className="absolute inset-[-2px] rounded-full z-[-1]"
            style={{
              background: `
                radial-gradient(circle at 50% 0%, hsl(27deg 93% 60%), transparent 70%),
                radial-gradient(circle at 100% 50%, #00a6ff, transparent 70%),
                radial-gradient(circle at 50% 100%, #ff0056, transparent 70%),
                radial-gradient(circle at 0% 50%, #6500ff, transparent 70%)
              `,
              opacity: 0.8,
            }}
          />
        </>
      )}

      <img
        src={photoUrl || "/default-avatar.png"}
        alt={user.username || user.first_name}
        className={`w-full h-full rounded-full border-2 border-[#282828] object-cover bg-[#232323] ${
          isClickable ? "hover:border-orange-500 transition-colors" : ""
        }`}
        style={{ position: "relative", zIndex: 1 }}
      />
    </div>
  );
}

// Main App Component
const PAGE_SIZE = 50;

const App = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [itemStack, setItemStack] = useState([]); // Стек для навигации между карточками по внутренним ссылкам
  const [error, setError] = useState(null);
  const [totalCount, setTotalCount] = useState(null);
  const [tgUser, setTgUser] = useState(null);
  const [showAdminPanel, setShowAdminPanel] = useState(false);
  const [showAccessDenied, setShowAccessDenied] = useState(false);
  const [showAddTorrentModal, setShowAddTorrentModal] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [profileUserId, setProfileUserId] = useState(null); // ID профиля для просмотра (null = свой)
  const [profileStack, setProfileStack] = useState([]); // Стек для навигации между профилями
  const [showSaveToFavoritesModal, setShowSaveToFavoritesModal] =
    useState(false);
  const [bookmarkTorrent, setBookmarkTorrent] = useState(null);
  const [favoriteIds, setFavoriteIds] = useState(new Set()); // Сет ID торрентов в избранном
  const [isGlobalSearch, setIsGlobalSearch] = useState(false);
  const [globalSearchToggleVisible, setGlobalSearchToggleVisible] =
    useState(true);
  const handleGlobalSearchToggleVisibility = useCallback((visible) => {
    setGlobalSearchToggleVisible(Boolean(visible));
  }, []);
  const [selectedTrackers, setSelectedTrackers] = useState([
    "rutracker",
    "nnmclub",
    "piratebay",
    "1337x",
    "rarbg",
  ]);
  const [globalSearchQuery, setGlobalSearchQuery] = useState("");
  const [top10Items, setTop10Items] = useState([]);
  const [showTopManageModal, setShowTopManageModal] = useState(false);
  const [topManageTorrent, setTopManageTorrent] = useState(null);
  const [showHelpModal, setShowHelpModal] = useState(false);
  const [trackerFilter, setTrackerFilter] = useState(null);
  const loaderRef = useRef(null);
  const { user, loading: userLoading, isAdmin, hasAccess, isTester } = useUser();
  const [appSurface, setAppSurface] = useState("cgpeers");

  // === Глобальный перехватчик ссылок ===
  // Внутренние deep-link'и (torrent_, profile_) открываются прямо в приложении,
  // t.me ссылки через openTelegramLink(), остальные через openLink()
  const openTorrentById = useCallback((torrentId) => {
    fetch(`/api/content/${torrentId}`)
      .then((res) => {
        if (res.ok) return res.json();
        throw new Error("Torrent not found");
      })
      .then((torrent) => {
        const newItem = {
          id: torrent.id,
          title: torrent.title,
          description: torrent.description,
          category: torrent.category,
          size: torrent.size,
          time: torrent.time,
          image_url: torrent.image_url,
          image_urls: torrent.image_urls,
          is_exclusive: torrent.is_exclusive,
          torrent_path: torrent.torrent_path,
          archive_message_ids: torrent.archive_message_ids,
        };
        // Если уже открыта карточка — сохраняем её в стек
        setSelectedItem((prev) => {
          if (prev) {
            setItemStack((stack) => [...stack, prev]);
          }
          return newItem;
        });
      })
      .catch((err) => console.error("Error loading torrent:", err));
  }, []);

  // Резолвит hash в telegram_id и вызывает callback с результатом
  const resolveProfileHash = useCallback((hash) => {
    // Принимаем только анонимные хэши, числовые ID игнорируем
    if (!isNaN(hash)) return Promise.resolve(null);

    const initData = window.Telegram?.WebApp?.initData || "";
    return fetch(`/api/users/profile-by-hash/${hash}`, {
      headers: { Authorization: `tma ${initData}` },
    })
      .then((res) => {
        if (res.ok) return res.json();
        throw new Error("Profile not found");
      })
      .then((data) => {
        if (data.profile?.telegram_id) {
          return String(data.profile.telegram_id);
        }
        return null;
      })
      .catch((err) => {
        console.error("Profile not found:", err);
        return null;
      });
  }, []);

  // Открыть профиль по хэшу (для внешних вызовов — startapp, link interceptor)
  const openProfileByHash = useCallback(
    (hash) => {
      resolveProfileHash(hash).then((resolvedId) => {
        if (resolvedId) {
          setProfileStack([]);
          setProfileUserId(resolvedId);
          setShowProfileModal(true);
        }
      });
    },
    [resolveProfileHash],
  );

  useEffect(() => {
    const handleLinkClick = (e) => {
      const anchor = e.target.closest("a[href]");
      if (!anchor) return;
      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("#") || href.startsWith("javascript:"))
        return;

      // Только внешние ссылки (http/https)
      if (href.startsWith("http://") || href.startsWith("https://")) {
        // Проверяем, является ли это внутренней ссылкой нашего мини-аппа
        const internalMatch = href.match(
          /t\.me\/CGPEERSSITE_BOT\/Cgpeersapp\?startapp=(\w+)/,
        );
        if (internalMatch) {
          e.preventDefault();
          e.stopPropagation();
          const startParam = internalMatch[1];

          if (startParam.startsWith("torrent_")) {
            const torrentId = startParam.replace("torrent_", "");
            if (torrentId && !isNaN(torrentId)) {
              openTorrentById(torrentId);
            }
          } else if (startParam.startsWith("profile_")) {
            const targetId = startParam.replace("profile_", "");
            if (targetId) {
              openProfileByHash(targetId);
            }
          }
          return;
        }

        e.preventDefault();
        e.stopPropagation();

        try {
          if (
            href.includes("t.me/") &&
            window.Telegram?.WebApp?.openTelegramLink
          ) {
            window.Telegram.WebApp.openTelegramLink(href);
          } else if (window.Telegram?.WebApp?.openLink) {
            window.Telegram.WebApp.openLink(href);
          } else {
            window.open(href, "_blank");
          }
        } catch (err) {
          console.error("Error opening link:", err);
          window.open(href, "_blank");
        }
      }
    };

    document.addEventListener("click", handleLinkClick, true);
    return () => document.removeEventListener("click", handleLinkClick, true);
  }, [openTorrentById, openProfileByHash]);

  // === Telegram BackButton для модальных окон ===
  useEffect(() => {
    const backButton = window.Telegram?.WebApp?.BackButton;
    if (!backButton) return;

    // Определяем, есть ли открытое модальное окно (приоритет закрытия)
    const hasModal =
      selectedItem ||
      showSaveToFavoritesModal ||
      showProfileModal ||
      showAdminPanel ||
      showAddTorrentModal ||
      showAccessDenied ||
      showHelpModal;

    if (hasModal) {
      backButton.show();
    } else {
      backButton.hide();
    }

    const handleBack = () => {
      // Закрываем модальные окна в порядке приоритета (самое верхнее первым)
      if (selectedItem) {
        // Если есть стек — возвращаемся к предыдущей карточке
        if (itemStack.length > 0) {
          setSelectedItem(itemStack[itemStack.length - 1]);
          setItemStack((s) => s.slice(0, -1));
        } else {
          setSelectedItem(null);
        }
      } else if (showSaveToFavoritesModal) {
        setShowSaveToFavoritesModal(false);
        setBookmarkTorrent(null);
      } else if (showAccessDenied) {
        setShowAccessDenied(false);
      } else if (showAddTorrentModal) {
        setShowAddTorrentModal(false);
      } else if (showProfileModal) {
        // Если есть стек профилей — возвращаемся к предыдущему
        if (profileStack.length > 0) {
          setProfileUserId(profileStack[profileStack.length - 1]);
          setProfileStack((s) => s.slice(0, -1));
        } else {
          setShowProfileModal(false);
          setProfileUserId(null);
        }
      } else if (showHelpModal) {
        setShowHelpModal(false);
      } else if (showAdminPanel) {
        setShowAdminPanel(false);
      }
    };

    backButton.onClick(handleBack);
    return () => backButton.offClick(handleBack);
  }, [
    selectedItem,
    itemStack,
    showSaveToFavoritesModal,
    showProfileModal,
    profileStack,
    showAdminPanel,
    showAddTorrentModal,
    showAccessDenied,
    showHelpModal,
  ]);

  // Обработка startapp параметра для открытия профиля или торрента по ссылке
  useEffect(() => {
    if (window.Telegram?.WebApp?.initDataUnsafe?.start_param) {
      const startParam = window.Telegram.WebApp.initDataUnsafe.start_param;

      // Формат: profile_HASH (только анонимные хэши)
      if (startParam.startsWith("profile_")) {
        const targetId = startParam.replace("profile_", "");
        if (targetId && isNaN(targetId)) {
          // Только анонимный формат: profile_6K2ANail (hash)
          // Числовые ID (profile_5472743372) игнорируются — просто открываем приложение
          const initData = window.Telegram?.WebApp?.initData || "";
          fetch(`/api/users/profile-by-hash/${targetId}`, {
            headers: { Authorization: `tma ${initData}` },
          })
            .then((res) => {
              if (res.ok) return res.json();
              throw new Error("Profile not found");
            })
            .then((data) => {
              if (data.profile?.telegram_id) {
                setProfileUserId(String(data.profile.telegram_id));
                setShowProfileModal(true);
              }
              // Если профиль не найден — просто не открываем профиль
            })
            .catch(() => {
              // Хэш не найден — профиль не существует, просто заходим в приложение
              console.log("Profile hash not found, ignoring");
            });
        }
      }

      // Формат: torrent_ID
      if (startParam.startsWith("torrent_")) {
        const torrentId = startParam.replace("torrent_", "");
        if (torrentId && !isNaN(torrentId)) {
          // Загружаем торрент по ID и открываем модальное окно
          fetch(`/api/content/${torrentId}`)
            .then((res) => {
              if (res.ok) return res.json();
              throw new Error("Torrent not found");
            })
            .then((torrent) => {
              // Преобразуем в формат карточки
              const card = {
                id: torrent.id,
                title: torrent.title,
                description: torrent.description,
                category: torrent.category,
                size: torrent.size,
                time: torrent.time,
                image_url: torrent.image_url,
                image_urls: torrent.image_urls,
                is_exclusive: torrent.is_exclusive,
                torrent_path: torrent.torrent_path,
                archive_message_ids: torrent.archive_message_ids,
              };
              setItemStack([]);
              setSelectedItem(card);
            })
            .catch((err) => {
              console.error("Error loading torrent by startapp:", err);
            });
        }
      }
    }
  }, []);

  // Функция для открытия своего профиля
  const handleOpenOwnProfile = useCallback(() => {
    setProfileUserId(null); // null = свой профиль
    setShowProfileModal(true);
  }, []);

  // Функция для открытия модала сохранения в избранное
  // При клике на bookmark - сразу добавляем в избранное и открываем модальное окно
  const handleBookmarkClick = useCallback(
    async (torrent) => {
      const numId = Number(torrent.id);
      const isAlreadyFavorite = favoriteIds.has(numId);

      // Если ещё не в избранном - сначала добавляем
      if (!isAlreadyFavorite && user?.telegram_id) {
        try {
          const headers = {
            "Content-Type": "application/json",
            Authorization: `tma ${window.Telegram?.WebApp?.initData || ""}`,
          };
          const res = await fetch(
            `/api/users/profile/${user.telegram_id}/favorites`,
            {
              method: "POST",
              headers,
              body: JSON.stringify({ torrent_id: torrent.id }),
            },
          );
          if (res.ok) {
            // Обновляем локальное состояние
            setFavoriteIds((prev) => {
              const newSet = new Set(prev);
              newSet.add(numId);
              return newSet;
            });
          }
        } catch (error) {
          console.error("Error adding to favorites:", error);
        }
      }

      // Открываем модальное окно
      setBookmarkTorrent(torrent);
      setShowSaveToFavoritesModal(true);
    },
    [favoriteIds, user?.telegram_id],
  );

  // Callback для обновления состояния избранного
  const handleFavoriteChange = useCallback((torrentId, isFavorite) => {
    setFavoriteIds((prev) => {
      const newSet = new Set(prev);
      const numId = Number(torrentId);
      if (isFavorite) {
        newSet.add(numId);
      } else {
        newSet.delete(numId);
      }
      return newSet;
    });
  }, []);

  // Загрузка списка избранных
  useEffect(() => {
    const loadFavorites = async () => {
      if (!user?.telegram_id) return;
      try {
        const headers = {
          Authorization: `tma ${window.Telegram?.WebApp?.initData || ""}`,
        };
        const res = await fetch(
          `/api/users/profile/${user.telegram_id}/favorites/ids`,
          { headers },
        );
        if (res.ok) {
          const data = await res.json();
          // Приводим к числам для корректного сравнения
          setFavoriteIds(new Set((data.ids || []).map(Number)));
        }
      } catch (error) {
        console.error("Error loading favorites:", error);
      }
    };
    loadFavorites();
  }, [user?.telegram_id]);

  // Загрузка TOP-10 элементов
  const fetchTop10 = useCallback(async () => {
    try {
      const res = await fetch("/api/content/top/items");
      if (res.ok) {
        const data = await res.json();
        setTop10Items(
          (data.items || []).map((t) => ({
            id: t.id,
            title: t.title,
            label: t.title,
            image: getHighResImageUrl(t.image_url),
            image_url: t.image_url,
            image_urls: t.image_urls,
            description: t.description,
            category: t.category,
            size: t.size,
            time: t.time,
            is_exclusive: t.is_exclusive,
            is_top: true,
            top_position: t.top_position,
            torrent_path: t.torrent_path,
            archive_message_ids: t.archive_message_ids,
          })),
        );
      }
    } catch (err) {
      console.error("Error loading TOP-10:", err);
    }
  }, []);

  useEffect(() => {
    fetchTop10();
  }, [fetchTop10]);

  // Обработчик клика на кнопку TOP (для карточек в ленте)
  const handleTopClick = useCallback((item) => {
    setTopManageTorrent(item);
    setShowTopManageModal(true);
  }, []);

  useEffect(() => {
    // Получаем данные пользователя из Telegram WebApp API
    if (
      window.Telegram &&
      window.Telegram.WebApp &&
      window.Telegram.WebApp.initDataUnsafe &&
      window.Telegram.WebApp.initDataUnsafe.user
    ) {
      setTgUser(window.Telegram.WebApp.initDataUnsafe.user);
    } else {
      // Для обычного web можно попробовать получить из localStorage или не показывать аватар
      setTgUser(null);
    }
  }, []);

  // Сбрасываем глобальный поисковый запрос при выключении глобального поиска
  useEffect(() => {
    if (!isGlobalSearch) {
      setGlobalSearchQuery("");
    }
  }, [isGlobalSearch]);

  const appendContentFilters = (params, search, categories, filter) => {
    if (search) {
      params.append("search", search);
    }
    if (categories.length > 0) {
      params.append("categories", categories.join(","));
    }
    if (user?.telegram_id) {
      params.append("telegram_id", user.telegram_id);
    }
    if (filter && isAdmin()) {
      params.append("tracker_replacement", "disabled");
      params.append("sort", filter === "disabled-oldest" ? "asc" : "desc");
    }
  };

  const getContentRequestHeaders = (filter) => {
    const headers = {
      Accept: "application/json",
      "Content-Type": "application/json",
    };
    const initData = window.Telegram?.WebApp?.initData;
    if (filter && isAdmin() && initData) {
      headers.Authorization = `tma ${initData}`;
    }
    return headers;
  };

  // Функция загрузки первой страницы контента (с возможным фильтром)
  const fetchInitialContent = async (
    searchQuery = "",
    categories = [],
    filter = null,
  ) => {
    setLoading(true);
    setItems([]);
    setCurrentPage(0);
    setHasMore(true);
    setError(null);
    try {
      const params = new URLSearchParams({
        offset: "0",
        limit: PAGE_SIZE.toString(),
      });
      appendContentFilters(params, searchQuery, categories, filter);
      const apiUrl = `/api/content?${params.toString()}`;
      const response = await fetch(apiUrl, {
        headers: getContentRequestHeaders(filter),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setItems(data.items || []);
      setTotalCount(typeof data.total === "number" ? data.total : null);
      setHasMore((data.items ? data.items.length : 0) === PAGE_SIZE);
      setCurrentPage(0);
    } catch (error) {
      setError("Error loading content");
    } finally {
      setLoading(false);
    }
  };

  // Функция загрузки следующей страницы контента (обернута в useCallback)
  const fetchMoreContent = React.useCallback(async () => {
    if (!hasMore || loadingMore) return;
    const nextPage = currentPage + 1;
    setLoadingMore(true);
    try {
      const params = new URLSearchParams({
        offset: (nextPage * PAGE_SIZE).toString(),
        limit: PAGE_SIZE.toString(),
      });
      appendContentFilters(
        params,
        searchQuery,
        selectedCategories,
        trackerFilter,
      );
      const apiUrl = `/api/content?${params.toString()}`;
      const response = await fetch(apiUrl, {
        headers: getContentRequestHeaders(trackerFilter),
      });
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      setItems((prev) => [...prev, ...(data.items || [])]);
      setTotalCount(typeof data.total === "number" ? data.total : null);
      setHasMore((data.items ? data.items.length : 0) === PAGE_SIZE);
      setCurrentPage(nextPage);
    } catch (error) {
      console.error("Error fetching more content:", error);
    } finally {
      setLoadingMore(false);
    }
  }, [
    hasMore,
    loadingMore,
    currentPage,
    searchQuery,
    selectedCategories,
    PAGE_SIZE,
    user?.telegram_id,
    trackerFilter,
  ]);

  // Обработка успешного добавления торрента
  const handleTorrentAdded = useCallback((newTorrent) => {
    // Обновляем список торрентов, добавляя новый в начало
    setItems((prevItems) => [newTorrent, ...prevItems]);
    setTotalCount((prevCount) => (prevCount ? prevCount + 1 : 1));
  }, []);

  // Первоначальная загрузка контента — ждём пока user загрузится, чтобы не было "вспышки" скрытых карточек
  useEffect(() => {
    if (userLoading) return;
    // В Telegram контексте ждём полной загрузки user с telegram_id
    if (!user?.telegram_id && window.Telegram?.WebApp?.initDataUnsafe?.user)
      return;
    fetchInitialContent(searchQuery, selectedCategories, trackerFilter);
  }, [
    searchQuery,
    selectedCategories,
    trackerFilter,
    user?.telegram_id,
    userLoading,
  ]);

  const showAppDock = Boolean(tgUser) && isTester();
  const isRipperSurface = showAppDock && appSurface === "ripper";
  const compactHeader =
    !isRipperSurface && !isAdmin() && !globalSearchToggleVisible;
  // Лента карточек размонтируется в Global Search и 3DRipper вместе с маркером
  // подгрузки. Без этой зависимости наблюдатель остаётся на удалённом узле.
  const contentFeedVisible = !isGlobalSearch && !isRipperSurface;

  // Настройка Intersection Observer для отслеживания прокрутки
  useEffect(() => {
    if (!contentFeedVisible) return;

    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !loadingMore && !loading) {
          fetchMoreContent();
        }
      },
      { threshold: 0.5 },
    );

    const currentLoaderRef = loaderRef.current;
    if (currentLoaderRef) {
      observer.observe(currentLoaderRef);
    }

    return () => {
      observer.disconnect();
    };
  }, [
    contentFeedVisible,
    hasMore,
    loadingMore,
    loading,
    fetchMoreContent,
  ]);

  const handleSearch = debounce((query) => {
    console.log("Search query changed to:", query);
    setSearchQuery(query);
    // Reset pagination when search query changes
    setCurrentPage(0);
    setItems([]);
    setHasMore(true);
  }, 300);

  const handleCategoryChange = (categories) => {
    console.log("Categories changed to:", categories);
    setSelectedCategories(categories);
    // Reset pagination when categories change
    setCurrentPage(0);
    setItems([]);
    setHasMore(true);
  };

  // Функция удаления торрента (только для админов)
  const handleDeleteTorrent = async (torrentId) => {
    if (!isAdmin()) return;

    try {
      const response = await fetch(`/api/admin/torrents/${torrentId}`, {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `tma ${window.Telegram.WebApp.initData}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        // Удаляем торрент из локального состояния
        setItems((prevItems) =>
          prevItems.filter((item) => item.id !== torrentId),
        );

        // Если был открыт modal с этим торрентом, закрываем его
        if (selectedItem && selectedItem.id === torrentId) {
          setItemStack([]);
          setSelectedItem(null);
        }

        // Показываем уведомление об успешном удалении
        showSuccessToast("Torrent deleted", "");
      } else {
        const error = await response.json();
        showErrorToast("Delete failed", error.error || "Unknown error");
      }
    } catch (error) {
      console.error("Error deleting torrent:", error);
      showErrorToast("Error", "Failed to delete torrent");
    }
  };

  // Функция для скачивания из глобального поиска
  const handleGlobalDownload = async (result) => {
    try {
      if (!tgUser?.id) {
        alert("Telegram user ID not found");
        return;
      }

      const response = await fetch("/api/global-download", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `tma ${window.Telegram?.WebApp?.initData || ""}`,
        },
        body: JSON.stringify({
          torrentId: result.id,
          torrentUrl: result.torrent_url,
          magnetLink: result.magnet_link,
          magnetKey: result.magnet_key,
          title: result.title,
          telegramId: tgUser.id,
        }),
      });

      const data = await response.json();

      if (data.success) {
        // Определяем тип отправки
        // Проверяем в правильном порядке: сначала torrent_file, потом magnet_link
        const isTorrentFile = data.method?.includes("torrent_file");
        const isMagnetLink = data.method === "magnet_link";

        if (isTorrentFile) {
          showSuccessToast("Torrent sent!", "Сheck the telegram bot");
        } else if (isMagnetLink) {
          showSuccessToast("Magnet link sent!", "Сheck the telegram bot");
        } else {
          showSuccessToast("Sent to Telegram!", "");
        }
      } else {
        showErrorToast("Error", data.message || "Failed to send torrent");
      }
    } catch (error) {
      console.error("Global download error:", error);
      showErrorToast("Error", "Failed to download torrent");
    }
  };

  // Функция обработки поиска с учетом режима
  const handleUnifiedSearch = debounce((query) => {
    console.log("Search query changed to:", query);

    if (isGlobalSearch) {
      setGlobalSearchQuery(query);
    } else {
      setSearchQuery(query);
      // Reset pagination when search query changes
      setCurrentPage(0);
      setItems([]);
      setHasMore(true);
    }
  }, 300);

  // Фильтрация теперь происходит на сервере, а сортировка встроена в API
  const filteredItems = items;

  // Если пользователь не авторизован через Telegram
  if (!tgUser) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-transparent text-white relative">
        <StarfieldBackground />
        <div className="p-8 rounded-xl bg-[#232323] border border-[#282828] shadow-lg flex flex-col items-center text-center relative z-10">
          <img
            src="/cgp_logo_app.png"
            alt="Avatar"
            className="mb-4 object-contain"
            style={{ width: 220, height: "auto" }}
          />
          <h2 className="text-2xl font-bold mb-2">Sign in with Telegram</h2>
          <p className="mb-4 text-[#aaa]">
            To access this app, please sign in via Telegram Mini App or WebApp.
          </p>
          <a
            href="https://t.me/CGPEERSSITE_BOT"
            className="px-6 py-2 rounded-lg bg-[#229ED9] text-white font-semibold hover:bg-[#1b7ca6] transition"
          >
            Open in Telegram
          </a>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`app-shell flex flex-col min-h-screen${showAppDock ? " has-tester-dock" : ""}${compactHeader ? " gs-toggle-hidden" : ""}`}
    >
      <StarfieldBackground />

      {/* Header (fixed, blur, search inside) */}
      <header
        className="fixed top-0 left-0 w-full z-40 border-b border-[#282828] shadow-lg"
        style={{
          backgroundColor: "rgba(24,24,24,0.85)",
          WebkitBackdropFilter: "blur(8px)",
          backdropFilter: "blur(10px)",
          borderBottomLeftRadius: "16px",
          borderBottomRightRadius: "16px",
        }}
      >
        <div
          className="flex flex-col items-center py-2 px-2 gap-1"
          style={{ maxWidth: 640, margin: "0 auto" }}
        >
          {/* Верхняя строка: аватарка слева, логотип по центру */}
          <div className="app-header-tools relative flex w-full items-center">
            <div className="app-logo-slot">
              {showAppDock ? (
                <HeaderWordmark variant={isRipperSurface ? "ripper" : "cgp"} />
              ) : (
                <img
                  src="/cgp_logo_app.png"
                  alt="CGPeers Logo"
                  style={{ height: 56, width: "auto", objectFit: "contain" }}
                />
              )}
            </div>
            {/* Аватарка слева, выровнена по левому краю поиска */}
            <div className="app-header-side relative z-10 flex items-center">
              <Avatar
                user={tgUser}
                onClick={handleOpenOwnProfile}
                isClickable={true}
              />
            </div>
            <div className="flex-1" />
            {/* Help button справа */}
            <div className="app-header-side relative z-10 flex items-center justify-end">
              <div className="relative mr-2 ml-1 flex items-center justify-center">
                <div className="help-btn-glow" aria-hidden="true" />
                <button
                  type="button"
                  onClick={() => setShowHelpModal(true)}
                  className="relative z-[1] flex items-center justify-center rounded-full transition-all duration-200 ease-out active:scale-90 hover:scale-105 hover:brightness-110"
                  style={{
                    width: 34,
                    height: 34,
                    background:
                      "radial-gradient(circle at 30% 30%, rgba(255,255,255,0.14), rgba(255,255,255,0.04))",
                    border: "1px solid rgba(255,255,255,0.12)",
                    boxShadow: "0 2px 10px rgba(0,0,0,0.35)",
                  }}
                  aria-label="Help"
                  title="Help"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                    className="text-white"
                  >
                    <path
                      fill="none"
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="M9.146 9.074a2.998 2.998 0 0 1 5.28-.838A3 3 0 0 1 12 13v1m0 7a9 9 0 1 1 0-18a9 9 0 0 1 0 18m.05-4v.1h-.1V17z"
                    />
                  </svg>
                </button>
              </div>
            </div>
          </div>
          {/* Вторая строка: поиск + фильтр, строго по центру, maxWidth = 640px */}
          <div className="app-header-search relative z-10 w-full flex justify-center">
            <div className="w-full" style={{ maxWidth: 640, margin: "0 auto" }}>
              <SearchBar
                onSearch={handleUnifiedSearch}
                onCategoryChange={handleCategoryChange}
                selectedCategories={selectedCategories}
                searchQuery={isGlobalSearch ? globalSearchQuery : searchQuery}
                totalCount={totalCount}
                onShowAccessDenied={() => setShowAccessDenied(true)}
                isGlobalSearch={isGlobalSearch}
                setIsGlobalSearch={setIsGlobalSearch}
                isAdmin={isAdmin()}
                selectedTrackers={selectedTrackers}
                onTrackerChange={setSelectedTrackers}
                searchAction={isRipperSurface ? "download" : "filter"}
                onGlobalSearchVisibilityChange={
                  handleGlobalSearchToggleVisibility
                }
              />
            </div>
          </div>
        </div>
      </header>

      {/* Main Content (with padding for fixed header) */}
      <main className="app-main p-4 md:ml-2 flex-grow">
        <div style={{ maxWidth: 1200, margin: "0 auto" }}>
          {/* Content based on search mode. 3DRipper is an empty stub for now. */}
          {isRipperSurface ? null : isGlobalSearch ? (
            <GlobalSearchResults
              searchQuery={globalSearchQuery}
              selectedTrackers={selectedTrackers}
              onDownloadTorrent={handleGlobalDownload}
              telegramId={tgUser?.id}
            />
          ) : (
            <>
              {trackerFilter && (
                <div className="mb-4 flex items-center justify-between gap-3 rounded-xl border border-amber-500/30 bg-amber-500/10 px-4 py-3">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-amber-200">
                      {getTrackerFilterLabel(trackerFilter)}
                    </div>
                    <div className="text-xs text-amber-200/70">
                      Showing torrents with tracker replacement disabled
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setTrackerFilter(null)}
                    className="shrink-0 rounded-lg bg-amber-500/20 px-3 py-1.5 text-xs font-medium text-amber-100 transition-colors hover:bg-amber-500/30"
                  >
                    Clear
                  </button>
                </div>
              )}

              {/* TOP-10 Carousel — hidden during search */}
              {!searchQuery &&
                selectedCategories.length === 0 &&
                !trackerFilter &&
                top10Items.length > 0 && (
                  <TopCarousel
                    items={top10Items}
                    onItemClick={(slide) => {
                      setItemStack([]);
                      setSelectedItem({
                        id: slide.id,
                        title: slide.title,
                        description: slide.description,
                        category: slide.category,
                        size: slide.size,
                        time: slide.time,
                        image_url: slide.image_url,
                        image_urls: slide.image_urls,
                        is_exclusive: slide.is_exclusive,
                        is_top: slide.is_top,
                        top_position: slide.top_position,
                        torrent_path: slide.torrent_path,
                        archive_message_ids: slide.archive_message_ids,
                      });
                    }}
                  />
                )}

              {filteredItems.length === 0 && !loading && (
                <div className="text-center py-10">
                  <h2
                    className="text-xl font-semibold"
                    style={{ color: "#999" }}
                  >
                    No results found
                  </h2>
                  <p className="text-sm" style={{ color: "#666" }}>
                    Try adjusting your search or filter settings.
                  </p>
                </div>
              )}

              {filteredItems.length > 0 && (
                <VirtualizedCardList
                  items={filteredItems}
                  onCardClick={(item) => {
                    setItemStack([]);
                    setSelectedItem(item);
                  }}
                  onCardDelete={handleDeleteTorrent}
                  onBookmarkClick={handleBookmarkClick}
                  onTopClick={handleTopClick}
                  favoriteIds={favoriteIds}
                />
              )}

              {/* Индикатор загрузки дополнительного контента */}
              <div
                ref={loaderRef}
                style={{ height: hasMore ? "100px" : "0px", marginTop: "20px" }}
                className="flex justify-center items-center"
              >
                {loadingMore && <LoadingSpinner />}
              </div>
            </>
          )}
        </div>

        {/* Основной индикатор загрузки (только для начальной загрузки) */}
        {!isRipperSurface && loading && <LoadingSpinner />}

        {/* Сообщение об ошибке */}
        {error && (
          <Toast message={error} type="error" onClose={() => setError(null)} />
        )}
      </main>
      {/* Footer */}
      <footer className="p-4 bg-[#181818] border-t border-[#282828] mt-auto">
        <div style={{ maxWidth: 640, margin: "0 auto", textAlign: "center" }}>
          <p className="text-sm" style={{ color: "#666" }}>
            &copy; 2026 CGPeers. All rights not reserved.
          </p>
        </div>
      </footer>

      {/* Help Modal */}
      <HelpModal
        isOpen={showHelpModal}
        onClose={() => setShowHelpModal(false)}
      />

      {/* Admin Panel */}
      {showAdminPanel && isAdmin() && (
        <AdminPanel
          telegramId={user?.telegram_id}
          onClose={() => setShowAdminPanel(false)}
          onOpenProfile={(userId) => {
            setProfileUserId(userId);
            setShowProfileModal(true);
          }}
        />
      )}

      {/* Add Torrent Modal */}
      {showAddTorrentModal && isAdmin() && (
        <AddTorrentModal
          isOpen={showAddTorrentModal}
          onClose={() => setShowAddTorrentModal(false)}
          onSuccess={handleTorrentAdded}
        />
      )}

      {/* Profile Modal */}
      {showProfileModal && (
        <ProfileModal
          key={profileUserId || "self"}
          isOpen={showProfileModal}
          onClose={() => {
            // Если есть стек профилей — возвращаемся к предыдущему
            if (profileStack.length > 0) {
              setProfileUserId(profileStack[profileStack.length - 1]);
              setProfileStack((s) => s.slice(0, -1));
            } else {
              setShowProfileModal(false);
              setProfileUserId(null);
            }
          }}
          telegramUser={tgUser}
          profileUserId={profileUserId}
          favoriteIds={favoriteIds}
          onOpenProfile={(hashOrId) => {
            if (isNaN(hashOrId)) {
              // Hash — резолвим через API, потом пушим в стек
              resolveProfileHash(hashOrId).then((resolvedId) => {
                if (resolvedId) {
                  setProfileStack((s) => [...s, profileUserId]);
                  setProfileUserId(resolvedId);
                }
              });
            } else {
              // Числовой ID (из connections) — пушим текущий в стек и переключаем
              setProfileStack((s) => [...s, profileUserId]);
              setProfileUserId(hashOrId);
            }
          }}
          onCardClick={(card) => {
            // Преобразуем карточку в формат для DetailModal
            setItemStack([]);
            setSelectedItem({
              id: card.id,
              title: card.title,
              description: card.description,
              category: card.badge,
              size: card.size,
              time: card.time,
              image_url: card.image,
              image_urls: card.image_urls,
              is_exclusive: card.isExclusive,
              torrent_path: card.torrent_path,
              archive_message_ids: card.archive_message_ids,
            });
          }}
          onBookmarkClick={(card) => {
            // Преобразуем карточку в формат для SaveToFavoritesModal
            const torrent = {
              id: card.id,
              title: card.title,
              description: card.description,
              category: card.badge,
              size: card.size,
              time: card.time,
              image_url: card.image,
              image_urls: card.image_urls,
              is_exclusive: card.isExclusive,
            };
            handleBookmarkClick(torrent);
          }}
        />
      )}

      {/* Save to Favorites Modal */}
      {showSaveToFavoritesModal && bookmarkTorrent && (
        <SaveToFavoritesModal
          isOpen={showSaveToFavoritesModal}
          onClose={() => {
            setShowSaveToFavoritesModal(false);
            setBookmarkTorrent(null);
          }}
          torrent={bookmarkTorrent}
          onFavoriteChange={handleFavoriteChange}
        />
      )}

      {/* TOP-10 Manage Modal */}
      {showTopManageModal && topManageTorrent && isAdmin() && (
        <TopManageModal
          isOpen={showTopManageModal}
          onClose={() => {
            setShowTopManageModal(false);
            setTopManageTorrent(null);
          }}
          torrent={topManageTorrent}
          onSuccess={(updatedItems) => {
            // Refresh top10 items
            fetchTop10();
          }}
        />
      )}

      {/* Detail Modal - рендерится последним чтобы быть поверх всех модальных окон */}
      {selectedItem && (
        <DetailModal
          key={selectedItem.id}
          item={selectedItem}
          onClose={() => {
            // Если есть стек — возвращаемся к предыдущей карточке
            if (itemStack.length > 0) {
              setSelectedItem(itemStack[itemStack.length - 1]);
              setItemStack((s) => s.slice(0, -1));
            } else {
              setSelectedItem(null);
            }
          }}
        />
      )}

      {/* Access Denied Modal */}
      <PremiumModalContainer
        isOpen={showAccessDenied}
        onClose={() => setShowAccessDenied(false)}
      />

      {/* Admin Buttons - только для администраторов */}
      {isAdmin() && (
        <div className="admin-fab-stack">
          {/* Admin Panel Button */}
          <button
            onClick={() => setShowAdminPanel(true)}
            className="bg-red-600 hover:bg-red-700 text-white p-3 rounded-full shadow-lg transition-colors flex items-center justify-center"
            title="Admin Panel"
          >
            <svg
              width="28"
              height="28"
              viewBox="0 0 28 28"
              fill="currentColor"
              style={{ display: "block" }}
            >
              <path d="M16.563 15.9c-.159-.052-1.164-.505-.536-2.414h-.009c1.637-1.686 2.888-4.399 2.888-7.07c0-4.107-2.731-6.26-5.905-6.26c-3.176 0-5.892 2.152-5.892 6.26c0 2.682 1.244 5.406 2.891 7.088c.642 1.684-.506 2.309-.746 2.396c-3.324 1.203-7.224 3.394-7.224 5.557v.811c0 2.947 5.714 3.617 11.002 3.617c5.296 0 10.938-.67 10.938-3.617v-.811c0-2.228-3.919-4.402-7.407-5.557m-5.516 8.709c0-2.549 1.623-5.99 1.623-5.99l-1.123-.881c0-.842 1.453-1.723 1.453-1.723s1.449.895 1.449 1.723l-1.119.881s1.623 3.428 1.623 6.018c0 .406-3.906.312-3.906-.028" />
            </svg>
          </button>

          {/* Add Torrent Button */}
          <button
            onClick={() => setShowAddTorrentModal(true)}
            className="bg-green-600 hover:bg-green-700 text-white p-3 rounded-full shadow-lg transition-colors flex items-center justify-center"
            title="Добавить торрент"
          >
            <Plus size={28} />
          </button>

          <AdminFilterButton
            value={trackerFilter}
            onChange={(nextFilter) => {
              setTrackerFilter(nextFilter);
              if (nextFilter) {
                setIsGlobalSearch(false);
              }
            }}
          />
        </div>
      )}

      {showAppDock && (
        <AppSwitcherBar mode={appSurface} onChange={setAppSurface} />
      )}

      {/* Back to Top Button - скрываем когда открыто модальное окно */}
      <BackToTopButton
        hideWhenModalOpen={!!selectedItem || showProfileModal}
      />
    </div>
  );
};

// Главный компонент приложения с провайдером пользователя
const AppWithProvider = () => {
  return (
    <UserProvider>
      <App />
    </UserProvider>
  );
};

export default AppWithProvider;
